import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom'; // eslint-disable-line no-unused-vars
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import { PaymentBadge } from '../components/Badges';
import {
  Search,
  Receipt,
  Save,
  IndianRupee,
  ArrowUpDown,
  Plus,
  History,
  FileText,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Clock,
  Printer,
  Layers,
  Stethoscope,
  Pill,
  Pencil,
} from 'lucide-react';
import BillPrintModal from '../components/BillPrintModal';
import PharmacyBillPrintModal from '../components/PharmacyBillPrintModal';
import ViewModeSwitcher, { useViewMode } from '../components/ViewModeSwitcher';
import { CLINIC_NAME, CLINIC_PHONE, CLINIC_ADDRESS } from '../utils/clinicConfig';
import WhatsAppButton from '../components/WhatsAppButton';
import DatePicker from '../components/DatePicker';
import { todayISO, toLocalISODate } from '../utils/clinicDate';

// ── helpers ───────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const SORT_OPTIONS = [
  { key: 'tokenDate-desc', label: 'Date (newest)' },
  { key: 'tokenDate-asc', label: 'Date (oldest)' },
  { key: 'name-asc',      label: 'Name (A–Z)' },
  { key: 'name-desc',     label: 'Name (Z–A)' },
  { key: 'paymentStatus-asc',  label: 'Pending first' },
  { key: 'paymentStatus-desc', label: 'Paid first' },
  { key: 'balance-desc',  label: 'Highest pending balance' },
  { key: 'amount-desc',   label: 'Highest bill amount' },
];

// DB stores 'unpaid'; we show "Pending" everywhere in the UI.
// This map is the single source of truth for display labels.
const STATUS_LABELS = { unpaid: 'Pending', partial: 'Partial', paid: 'Paid' };

const FILTER_OPTIONS = [
  { key: 'all',    label: 'All' },
  { key: 'unpaid', label: 'Pending' },   // display "Pending", pass 'unpaid' to DB
  { key: 'partial',label: 'Partial' },
  { key: 'paid',   label: 'Paid' },
];

// "Pending" must mean "still owes something" — unpaid AND partial, not just
// unpaid. A patient who's made a partial payment but still has a large
// amount outstanding (e.g. paid ₹5,000 of ₹23,000) was previously invisible
// under this filter, since their status is 'partial', not 'unpaid' — the
// exact opposite of what "Pending" should surface. "Partial" stays
// available on its own for anyone who wants that narrower slice.
function matchesStatusFilter(actualStatus, filterKey) {
  if (filterKey === 'all') return true;
  if (filterKey === 'unpaid') return actualStatus === 'unpaid' || actualStatus === 'partial';
  return actualStatus === filterKey;
}

// Round monetary value to 2 decimal places to prevent floating point drift
// e.g. 15000 * 1.18 = 17699.999999999996 → stored/compared as 17700.00
function roundMoney(n) {
  return Math.round((n || 0) * 100) / 100;
}

function netTotal(billing) {
  const amount   = roundMoney(billing?.amount   || 0);
  const discount = roundMoney(billing?.discount || 0);
  const taxPct   = Number(billing?.taxPercent   || 0);
  const afterDiscount = Math.max(roundMoney(amount - discount), 0);
  return roundMoney(afterDiscount + afterDiscount * (taxPct / 100));
}

function pendingBalance(billing) {
  const net  = netTotal(billing);
  const paid = roundMoney(billing?.amountPaid || 0);
  return Math.max(roundMoney(net - paid), 0);
}

// Derive payment status automatically from amounts.
// This is called whenever amount/discount/tax/amountPaid changes.
function deriveStatus(net, amountPaid) {
  const paid  = roundMoney(Number(amountPaid) || 0);
  const total = roundMoney(Number(net) || 0);
  if (total === 0) return 'unpaid';
  if (paid >= total) return 'paid';
  if (paid > 0) return 'partial';
  return 'unpaid';
}

function fmt(n) {
  // Explicit Math.round avoids banker's rounding in toLocaleString
  return Math.round(n || 0).toLocaleString('en-IN');
}

// todayISO imported from utils/clinicDate (see top of file) — was
// previously defined locally here using new Date().toISOString(), which
// computes the UTC date, not the local one (see that file for why this
// silently blocked "today" from being selectable for part of every day).

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}





// ── Bill WhatsApp message builder ─────────────────────────────────────────────
function buildBillMessage(p, total, paid, pending) {
  const clinic   = typeof CLINIC_NAME    !== 'undefined' ? CLINIC_NAME    : 'Our Clinic';
  const phone    = typeof CLINIC_PHONE   !== 'undefined' ? CLINIC_PHONE   : '';
  const address  = typeof CLINIC_ADDRESS !== 'undefined' ? CLINIC_ADDRESS : '';
  const status   = p.billing?.paymentStatus === 'paid' ? 'PAID IN FULL'
                 : p.billing?.paymentStatus === 'partial' ? 'PARTIALLY PAID'
                 : 'PAYMENT DUE';
  return [
    `*${clinic}*`,
    address ? address : null,
    ``,
    `*BILLING SUMMARY*`,
    `Patient : ${p.name}`,
    `Date    : ${p.tokenDate}`,
    p.appointmentSlot ? `Slot    : ${p.appointmentSlot}` : null,
    p.billing?.invoiceNumber ? `Invoice : ${p.billing.invoiceNumber}` : null,
    ``,
    `Total   : Rs. ${fmt(total)}`,
    paid > 0 ? `Paid    : Rs. ${fmt(paid)}` : null,
    pending > 0 ? `*Pending : Rs. ${fmt(pending)}*` : null,
    `Status  : ${status}`,
    ``,
    pending > 0 ? `Kindly clear the pending balance at your earliest convenience.` : `Thank you for your payment!`,
    phone ? `For queries, call ${phone}.` : null,
  ].filter(l => l !== null).join('\n');
}

// ── BillingPatientCard — single visit row used in date/status grouped views ──
function BillingPatientCard({ p, isAdmin, editingId, setEditingId, correctionId, setCorrectionId, startEdit, startCorrection, setPrintBillPatient, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, saveBilling, recordPayment, correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection, plain }) {
  const total        = netTotal(p.billing);
  const pending      = pendingBalance(p.billing);
  const isEditing    = editingId    === p._id;
  const isCorrecting = correctionId === p._id;
  return (
    <div className={plain ? undefined : 'card'} style={{ padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
            {p.name}
          </div>
          <div style={{ fontSize:12, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)', marginTop:2 }}>
            {p.patientUID} · Token #{p.tokenNumber} · {p.tokenDate}
            {p.billing?.invoiceNumber ? ` · ${p.billing.invoiceNumber}` : ''}
          </div>
          <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
            {p.age && <span>{p.age}y, {p.gender}</span>}
            {p.phone && <span>Ph: {p.phone}</span>}
            {p.appointmentSlot && <span style={{ color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}</span>}
            {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
          </div>
          <div style={{ fontSize:12, marginTop:2 }}>
            {(p.billing?.amountPaid||0) > 0 && <span style={{ color:'var(--color-success)' }}>✓ Paid ₹{fmt(p.billing.amountPaid)}</span>}
            {pending > 0 && <span style={{ marginLeft:12, color:'var(--color-warning)' }}>⏳ Pending ₹{fmt(pending)}</span>}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>TOTAL</div>
            <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)' }}>₹{fmt(total)}</div>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {p.phone && (
              <WhatsAppButton
                patient={{ ...p, name: p.name, phone: p.phone,
                  _billMessage: buildBillMessage(p, total, p.billing?.amountPaid||0, pending) }}
                slot={p.appointmentSlot}
                date={p.tokenDate}
                tokenNumber={p.tokenNumber}
                size="xs"
                billMode
              />
            )}
            <button className="btn btn-outline btn-sm" onClick={() => setPrintBillPatient(p)}><Printer size={13}/> Print</button>
            <button className="btn btn-outline btn-sm" onClick={() => isEditing ? setEditingId(null) : startEdit(p)}>{isEditing ? 'Close' : 'Manage'}</button>
            {isAdmin && <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(p)}><ShieldAlert size={13}/>{isCorrecting ? 'Close' : 'Correct'}</button>}
          </div>
        </div>
      </div>
      {isEditing && (
        <div style={{ marginTop:12 }}>
          <ManagePanel p={p} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(p._id, p)} onRecordPayment={() => recordPayment(p)} />
        </div>
      )}
      {isCorrecting && isAdmin && (
        <div style={{ marginTop:12 }}>
          <CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(p._id)} />
        </div>
      )}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────
// ── BillingCardGroup — renders a list of patient cards with listMode awareness ─
function BillingCardGroup({ patients, sortKey, listMode, isAdmin, editingId, setEditingId, correctionId, setCorrectionId, startEdit, startCorrection, setPrintBillPatient, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, saveBilling, recordPayment, correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection, nested }) {
  const sorted = [...patients].sort((a,b) => {
    const [key, dir] = (sortKey||'tokenDate-desc').split('-');
    const mult = dir === 'asc' ? 1 : -1;
    if (key === 'name') return a.name.localeCompare(b.name) * mult;
    if (key === 'balance') return (Math.max(netTotal(a.billing)-(a.billing?.amountPaid||0),0) - Math.max(netTotal(b.billing)-(b.billing?.amountPaid||0),0)) * mult;
    if (key === 'amount') return (netTotal(a.billing) - netTotal(b.billing)) * mult;
    const av = a.tokenDate||'', bv = b.tokenDate||'';
    return av < bv ? -1*mult : av > bv ? 1*mult : 0;
  });

  const cardProps = { isAdmin, editingId, setEditingId, correctionId, setCorrectionId, startEdit, startCorrection, setPrintBillPatient, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, saveBilling, recordPayment, correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection };

  if (listMode === 'compact') {
    return (
      <div className={nested ? undefined : 'card'} style={{ overflow:'hidden' }}>
        {sorted.map((p,i) => {
          const total   = netTotal(p.billing);
          const pending = pendingBalance(p.billing);
          const isEd = editingId    === p._id;
          const isCo = correctionId === p._id;
          return (
            <div key={p._id}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', borderTop:i===0?'none':'1px solid var(--color-border)', background:i%2===0?'transparent':'var(--color-surface)' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <span style={{ fontWeight:700, fontSize:13 }}>{p.name}</span>
                  <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}</span>
                  {p.phone && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}> · {p.phone}</span>}
                  <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>📅 {p.tokenDate}</span>
                  {p.appointmentSlot && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-primary)', fontWeight:600 }}>🕐 {p.appointmentSlot}</span>}
                </div>
                {pending>0 && <span style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmt(pending)} pending</span>}
                <span style={{ fontSize:13, fontWeight:800, color:'var(--color-primary)' }}>₹{fmt(total)}</span>
                <div style={{ display:'flex', gap:5, alignItems:'center', flexShrink:0 }}>
                  {p.phone && <WhatsAppButton patient={{...p, _billMessage: buildBillMessage(p, total, p.billing?.amountPaid||0, pending)}} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" billMode />}
                  <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setPrintBillPatient(p)}><Printer size={12}/></button>
                  <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => isEd ? setEditingId(null) : startEdit(p)}>{isEd ? 'Close' : 'Manage'}</button>
                  {isAdmin && <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCo ? setCorrectionId(null) : startCorrection(p)}>{isCo ? 'Close' : 'Correct'}</button>}
                </div>
              </div>
              {isEd && <div style={{ padding:'0 14px 12px' }}><ManagePanel p={p} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(p._id, p)} onRecordPayment={() => recordPayment(p)} isAdmin={isAdmin} /></div>}
              {isCo && isAdmin && <div style={{ padding:'0 14px 12px' }}><CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(p._id)} /></div>}
            </div>
          );
        })}
      </div>
    );
  }

  if (listMode === 'grid') {
    return (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12, padding: nested ? 12 : 0 }}>
        {sorted.map((p, i) => {
          const total   = netTotal(p.billing);
          const pending = pendingBalance(p.billing);
          const isEd = editingId    === p._id;
          const isCo = correctionId === p._id;
          return (
            <div key={p._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
              <div className={`tilt3d-card${nested ? '' : ' card'}`} style={{ padding:14, display:'flex', flexDirection:'column', gap:8, border: nested ? '1px solid var(--color-border)' : undefined, borderRadius: nested ? 8 : undefined }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:6 }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}</div>
                  {p.phone && <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>📱 {p.phone}</div>}
                  <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>{p.tokenDate}</div>
                  {p.appointmentSlot && <div style={{ fontSize:12, color:'var(--color-primary)', fontWeight:600 }}>🕐 {p.appointmentSlot}</div>}
                  {pending>0 && <div style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmt(pending)} pending</div>}
                </div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center', marginTop:'auto', paddingTop:4, borderTop:'1px solid var(--color-border)' }}>
                  <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)', flex:1 }}>₹{fmt(total)}</div>
                  {p.phone && <WhatsAppButton patient={{...p, _billMessage: buildBillMessage(p, total, p.billing?.amountPaid||0, pending)}} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" billMode />}
                  <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setPrintBillPatient(p)}><Printer size={12}/></button>
                  <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => isEd ? setEditingId(null) : startEdit(p)}>{isEd ? 'Close' : 'Manage'}</button>
                  {isAdmin && <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCo ? setCorrectionId(null) : startCorrection(p)}>{isCo ? 'Close' : 'Correct'}</button>}
                </div>
              </div>
              {isEd && <div style={{ marginTop:4 }}><ManagePanel p={p} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(p._id, p)} onRecordPayment={() => recordPayment(p)} isAdmin={isAdmin} /></div>}
              {isCo && isAdmin && <div style={{ marginTop:4 }}><CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(p._id)} /></div>}
            </div>
          );
        })}
      </div>
    );
  }

  // Default: list view
  return (
    <div style={{ display:'grid', gap: nested ? 0 : 8 }}>
      {sorted.map((p,i) => (
        <div key={p._id} style={{ borderTop: nested && i>0 ? '1px solid var(--color-border)' : 'none' }}>
          <BillingPatientCard p={p} {...cardProps} plain={nested} />
        </div>
      ))}
    </div>
  );
}

// ── PharmacyBillingView — mirrors Treatment Billing exactly ──────────────────
// Same toolbar, stats, filters, group-by, sort, view modes, print, WhatsApp.
// Data source: /pharmacy/transactions?type=outgoing (patient-linked only).

const PHARM_SORT_OPTIONS = [
  { key: 'date-desc',   label: 'Date (newest)' },
  { key: 'date-asc',    label: 'Date (oldest)' },
  { key: 'name-asc',    label: 'Name (A–Z)' },
  { key: 'name-desc',   label: 'Name (Z–A)' },
  { key: 'amount-desc', label: 'Highest amount' },
  { key: 'amount-asc',  label: 'Lowest amount' },
];

const PHARM_STATUS_OPTIONS = [
  { key: 'all',     label: 'All' },
  { key: 'unpaid',  label: 'Pending' },
  { key: 'partial', label: 'Partial' },
  { key: 'paid',    label: 'Paid' },
];

// WhatsApp message builder for pharmacy
function buildPharmWAMessage(group, fmtFn) {
  const clinic = typeof CLINIC_NAME  !== 'undefined' ? CLINIC_NAME  : 'Our Clinic';
  const phone  = typeof CLINIC_PHONE !== 'undefined' ? CLINIC_PHONE : '';
  const total = group.items.reduce((s,t) => s + (t.quantity||0)*(t.sellingPrice||0), 0);
  const lines = group.items.map(t =>
    `  • ${t.itemName} ×${t.quantity} = ₹${fmtFn((t.quantity||0)*(t.sellingPrice||0))}`
  ).join('\n');
  return [
    `*${clinic}*`,
    `Greetings from ${clinic}!`,
    ``,
    `*PHARMACY BILL*`,
    `Patient: ${group.name}${group.uid ? ' ('+group.uid+')' : ''}`,
    lines,
    ``,
    `*Total: ₹${fmtFn(total)}*`,
    phone ? `` : null,
    phone ? `For queries, call ${phone}.` : null,
  ].filter(l => l !== null).join('\n');
}

function PharmacyBillingView() {
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  // ── state ──────────────────────────────────────────────────────────────────
  const [transactions, setTransactions] = React.useState([]);
  const [loading, setLoading]           = React.useState(true);
  // Remember the last selected date until the user manually changes it —
  // was resetting to "all" on every visit/reload before.
  const PHARM_BILLING_DATE_KEY = 'pharmbilling_selectedDate';
  const PHARM_BILLING_DATES_KEY = 'pharmbilling_selectedDates';
  const [selectedDate, setSelectedDateRaw] = React.useState(() => localStorage.getItem(PHARM_BILLING_DATE_KEY) || 'all');
  const [selectedDates, setSelectedDatesRaw] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PHARM_BILLING_DATES_KEY) || 'null');
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* ignore malformed storage */ }
    return new Set();
  });
  function setSelectedDate(d) {
    setSelectedDateRaw(d);
    localStorage.setItem(PHARM_BILLING_DATE_KEY, d);
    const newDates = d === 'all' ? new Set() : new Set([d]);
    setSelectedDatesRaw(newDates);
    localStorage.setItem(PHARM_BILLING_DATES_KEY, JSON.stringify([...newDates]));
  }
  function setSelectedDates(set) {
    setSelectedDatesRaw(set);
    localStorage.setItem(PHARM_BILLING_DATES_KEY, JSON.stringify([...set]));
    const d = set.size === 1 ? [...set][0] : 'all';
    setSelectedDateRaw(d);
    localStorage.setItem(PHARM_BILLING_DATE_KEY, d);
  }
  const [availableDates, setAvailableDates] = React.useState(new Set());
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [search, setSearch]             = React.useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => searchParams.get('search') || '');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);
  const [sortKey, setSortKey]           = React.useState(() => localStorage.getItem('pharmbilling_sort') || 'date-desc');
  const [groupBy, setGroupBy]           = React.useState(() => localStorage.getItem('pharmbilling_groupby') || 'patient');
  const [listMode, setListMode]         = useViewMode('pharmbilling_view', 'list');
  const [showStats, setShowStats]       = React.useState(() => localStorage.getItem('pharmbilling_showstats') !== 'false');
  const [page, setPage]                 = React.useState(1);
  const [limit, setLimit]               = React.useState(25);
  // Patient for the pharmacy-only bill print modal (branded, patient-details, pharmacy items only)
  const [printPharmacyBillPatient, setPrintPharmacyBillPatient] = React.useState(null);

  // ── Manage / Correct — pharmacy payment tracking, separate from Treatment Billing ──
  const [managingUid, setManagingUid]     = React.useState(null);
  const [correctingUid, setCorrectingUid] = React.useState(null);
  const [billingRecords, setBillingRecords] = React.useState({}); // uid -> { amountPaid, paymentStatus, notes, paymentHistory, owed }
  const [billingForm, setBillingForm]     = React.useState({ notes: '', discount: 0 });
  const [paymentForm, setPaymentForm]     = React.useState({ amount:'', method:'cash', note:'', date: todayISO() });
  const [correctionForm, setCorrectionForm] = React.useState({ amountPaid:'', discount:'', notes:'' });
  const [correctionReason, setCorrectionReason] = React.useState('');
  const [saving, setSaving]                     = React.useState(false);
  const [recordingPayment, setRecordingPayment] = React.useState(false);
  const [correcting, setCorrecting]             = React.useState(false);

  async function loadBillingRecord(uid) {
    try {
      const res = await api.get(`/pharmacy/billing/${uid}`);
      setBillingRecords(prev => ({ ...prev, [uid]: res.data.billing }));
      return res.data.billing;
    } catch { showToast('Could not load pharmacy billing details.', 'error'); return null; }
  }

  async function startManage(g) {
    if (managingUid === g.uid) { setManagingUid(null); return; }
    setManagingUid(g.uid); setCorrectingUid(null);
    const rec = billingRecords[g.uid] || await loadBillingRecord(g.uid);
    setBillingForm({ notes: rec?.notes || '', discount: rec?.discount || 0 });
    setPaymentForm({ amount:'', method:'cash', note:'' });
  }

  async function startCorrection(g) {
    if (correctingUid === g.uid) { setCorrectingUid(null); return; }
    setCorrectingUid(g.uid); setManagingUid(null);
    const rec = billingRecords[g.uid] || await loadBillingRecord(g.uid);
    setCorrectionForm({ amountPaid: rec?.amountPaid ?? 0, discount: rec?.discount ?? 0, notes: rec?.notes || '' });
    setCorrectionReason('');
  }

  async function saveNotes(g) {
    setSaving(true);
    try {
      const res = await api.put(`/pharmacy/billing/${g.uid}`, { notes: billingForm.notes, discount: Number(billingForm.discount)||0, patientName: g.name });
      setBillingRecords(prev => ({ ...prev, [g.uid]: res.data.billing }));
      showToast('Saved.', 'success');
    } catch (err) { showToast(err.response?.data?.message || 'Could not save.', 'error'); }
    finally { setSaving(false); }
  }

  async function recordPayment(g) {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) { showToast('Enter a payment amount.', 'error'); return; }
    setRecordingPayment(true);
    try {
      const res = await api.put(`/pharmacy/billing/${g.uid}/payment`, {
        patientName: g.name, amount: Number(paymentForm.amount), method: paymentForm.method, note: paymentForm.note,
        paidAt: paymentForm.date || undefined,
      });
      setBillingRecords(prev => ({ ...prev, [g.uid]: res.data.billing }));
      setPaymentForm({ amount:'', method:'cash', note:'', date: todayISO() });
      showToast('Payment recorded.', 'success');
    } catch (err) { showToast(err.response?.data?.message || 'Could not record payment.', 'error'); }
    finally { setRecordingPayment(false); }
  }

  async function applyCorrection(g) {
    setCorrecting(true);
    try {
      const res = await api.put(`/pharmacy/billing/${g.uid}/correction`, {
        patientName: g.name,
        amountPaid: Number(correctionForm.amountPaid),
        discount: Number(correctionForm.discount) || 0,
        notes: correctionForm.notes,
        correctionReason,
      });
      setBillingRecords(prev => ({ ...prev, [g.uid]: res.data.billing }));
      showToast('Correction applied.', 'success');
      setCorrectingUid(null);
    } catch (err) { showToast(err.response?.data?.message || 'Could not apply correction.', 'error'); }
    finally { setCorrecting(false); }
  }

  // ── load ───────────────────────────────────────────────────────────────────
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: 'outgoing', limit: 1000 };
      // The backend already combines search + date independently — send
      // the date whenever one is actually selected, so "search a name AND
      // narrow to one date" works, instead of a selected date silently
      // having no effect while search text is present.
      if (selectedDates.size > 1) params.dates = [...selectedDates].join(',');
      else if (selectedDate !== 'all') params.date = selectedDate;
      const res = await api.get('/pharmacy/transactions', { params });
      const all = (res.data.transactions || []).filter(t => t.patientUID || t.patientName);
      setTransactions(all);
      // Use the backend's availableDates (computed from an UNFILTERED query)
      // rather than deriving it from `all` here — `all` is scoped to
      // whatever date is currently selected, so building the calendar's
      // available-dates set from it would collapse to just that one date,
      // disabling every other date until "All" was clicked to escape.
      if (res.data.availableDates) setAvailableDates(new Set(res.data.availableDates));
    } catch { showToast('Could not load pharmacy billing.', 'error'); }
    finally { setLoading(false); }
  }, [selectedDate, selectedDates, showToast]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { setPage(1); }, [selectedDate, selectedDates, limit, statusFilter, debouncedSearch, sortKey, groupBy]);

  // ── bulk payment-status lookup ─────────────────────────────────────────────
  // Fetch owed/paid/paymentStatus for every patient in the current transaction
  // set in ONE request, so the status filter and stats reflect real payment
  // progress (from Manage/Correct) instead of a per-sale flag.
  React.useEffect(() => {
    const uids = [...new Set(transactions.map(t => t.patientUID).filter(Boolean))];
    if (uids.length === 0) return;
    let cancelled = false;
    api.get('/pharmacy/billing', { params: { uids: uids.join(',') } })
      .then(res => { if (!cancelled) setBillingRecords(prev => ({ ...prev, ...res.data.billing })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  function statusOf(uid) {
    return billingRecords[uid]?.paymentStatus || 'unpaid';
  }

  // ── filter ─────────────────────────────────────────────────────────────────
  const afterFilter = React.useMemo(() => {
    let list = [...transactions];
    if (statusFilter !== 'all') list = list.filter(t => matchesStatusFilter(statusOf(t.patientUID), statusFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.patientName||'').toLowerCase().includes(q) ||
        (t.patientUID||'').toLowerCase().includes(q)  ||
        (t.itemName||'').toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, statusFilter, search, billingRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── sort ───────────────────────────────────────────────────────────────────
  const afterSort = React.useMemo(() => {
    const [field, dir] = sortKey.split('-');
    const m = dir === 'asc' ? 1 : -1;
    return [...afterFilter].sort((a,b) => {
      if (field === 'name')   return ((a.patientName||'').localeCompare(b.patientName||'')) * m;
      if (field === 'amount') return ((a.quantity||0)*(a.sellingPrice||0) - (b.quantity||0)*(b.sellingPrice||0)) * m;
      return ((a.date||'') < (b.date||'') ? -1 : 1) * m; // date
    });
  }, [afterFilter, sortKey]);

  // ── pagination ─────────────────────────────────────────────────────────────
  const totalCount = afterSort.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // ── stats ──────────────────────────────────────────────────────────────────
  // "Total Sales" is date-scoped (matches the current date filter, like the
  // transaction list itself). Collected/Pending are each patient's real
  // running pharmacy balance — not tied to a single sale date — shown as
  // running totals for whichever patients are currently in view.
  const totalValue = React.useMemo(() => afterFilter.reduce((s,t) => s+(t.quantity||0)*(t.sellingPrice||0), 0), [afterFilter]);
  const statsUids  = React.useMemo(() => [...new Set(afterFilter.map(t=>t.patientUID).filter(Boolean))], [afterFilter]);
  const collectedValue = statsUids.reduce((s,uid) => s + (billingRecords[uid]?.amountPaid || 0), 0);
  const owedValue       = statsUids.reduce((s,uid) => s + (billingRecords[uid]?.owed || 0), 0);
  const pendingValue    = Math.max(owedValue - collectedValue, 0);

  // ── patient groups (for group-by-patient) ─────────────────────────────────
  const patientGroups = React.useMemo(() => {
    const map = {};
    afterSort.forEach(t => {
      const k = t.patientUID || t.patientName || 'Unknown';
      if (!map[k]) map[k] = { uid: t.patientUID||'', name: t.patientName||'Unknown', items: [] };
      map[k].items.push(t);
    });
    return Object.values(map);
  }, [afterSort]);

  // ── page patient groups ────────────────────────────────────────────────────
  const pagedPatientGroups = React.useMemo(() => {
    return patientGroups.slice((page-1)*limit, page*limit);
  }, [patientGroups, page, limit]);

  const patientTotalCount = patientGroups.length;
  const patientTotalPages = Math.max(1, Math.ceil(patientTotalCount / limit));

  // ── patient phone lookup ───────────────────────────────────────────────────
  // Pharmacy transactions don't store the patient's phone number (only
  // name/UID), so we fetch it once per patient UID for whichever patients
  // are currently shown on the page, and cache it for the WhatsApp button
  // and the patient-details display.
  const [patientPhones, setPatientPhones] = React.useState({});
  React.useEffect(() => {
    const uids = [...new Set(pagedPatientGroups.map(g => g.uid).filter(Boolean))]
      .filter(uid => !(uid in patientPhones));
    if (uids.length === 0) return;
    let cancelled = false;
    Promise.all(uids.map(uid =>
      api.get('/patients', { params: { search: uid, limit: 1 } })
        .then(res => [uid, (res.data.patients || []).find(p => p.patientUID === uid)?.phone || ''])
        .catch(() => [uid, ''])
    )).then(results => {
      if (cancelled) return;
      setPatientPhones(prev => {
        const next = { ...prev };
        results.forEach(([uid, phone]) => { next[uid] = phone; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [pagedPatientGroups]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── date groups ────────────────────────────────────────────────────────────
  const dateGroups = React.useMemo(() => {
    if (groupBy !== 'date') return null;
    const map = {};
    afterSort.forEach(t => {
      const d = t.date?.slice(0,10) || 'No date';
      if (!map[d]) map[d] = [];
      map[d].push(t);
    });
    return Object.entries(map).sort(([a],[b]) => b > a ? 1 : -1);
  }, [afterSort, groupBy]);

  // ── status groups (real payment status — Pending / Partial / Paid) ────────
  const statusGroups = React.useMemo(() => {
    if (groupBy !== 'status') return null;
    const buckets = [['unpaid','Pending'],['partial','Partial'],['paid','Paid']];
    return buckets.map(([key,label]) => ({
      key, label,
      items: afterSort.filter(t => statusOf(t.patientUID) === key),
    }));
  }, [afterSort, groupBy, billingRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── single transaction row (used in all group views) ──────────────────────
  // ── pagination control ─────────────────────────────────────────────────────
  const usedTotal = groupBy === 'patient' ? patientTotalCount : totalCount;
  const usedPages = groupBy === 'patient' ? patientTotalPages : totalPages;

  // ── render ─────────────────────────────────────────────────────────────────
  const sharedPharmProps = {
    isAdmin, billingRecords, patientPhones, managingUid, correctingUid,
    setPrintPharmacyBillPatient, startManage, startCorrection,
    billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment,
    correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection,
    saveNotes, recordPayment,
  };

  return (
    <div>
      {/* Stats — same style as Treatment Billing */}
      {isAdmin && (
        <div style={{ marginBottom: showStats ? 0 : 8 }}>
          <button className="btn btn-outline btn-sm"
            style={{ marginBottom: showStats ? 12 : 0, display:'flex', alignItems:'center', gap:6 }}
            onClick={() => { const n=!showStats; setShowStats(n); localStorage.setItem('pharmbilling_showstats',n); }}>
            {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
          </button>
          {showStats && (
            <div className="stat-grid">
              <StatCard icon={IndianRupee} color="primary"
                label={selectedDate==='all' ? 'Total Sales (all time)' : `Total Sales — ${selectedDate}`}
                value={`₹${fmtP(totalValue)}`} />
              <StatCard icon={IndianRupee} color="success"
                label="Collected (all time)"
                value={`₹${fmtP(collectedValue)}`} />
              <StatCard icon={IndianRupee} color="warning"
                label="Pending Balance (all time)"
                value={`₹${fmtP(pendingValue)}`} />
            </div>
          )}
        </div>
      )}

      {/* Controls row — identical layout to Treatment Billing */}
      <div style={{ display:'flex', gap:12, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <ViewModeSwitcher mode={listMode} onChange={setListMode} />
        <DatePicker
          selectedDate={selectedDate}
          selectedDates={selectedDates}
          onChange={setSelectedDate}
          onMultiChange={setSelectedDates}
          availableDates={availableDates}
          multiSelect
        />
        <div style={{ position:'relative', flex:'1 1 200px' }}>
          <Search size={15} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)' }} />
          <input className="input" style={{ paddingLeft:32 }}
            placeholder="Search patient or item…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ position:'relative', minWidth:200 }}>
          <ArrowUpDown size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', pointerEvents:'none' }} />
          <select className="select" style={{ paddingLeft:30, fontWeight:600 }} value={sortKey}
            onChange={e => { setSortKey(e.target.value); localStorage.setItem('pharmbilling_sort', e.target.value); }}>
            {PHARM_SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Layers size={14} color="var(--color-ink-faint)" />
          <select className="select" style={{ minWidth:150, fontWeight:600 }} value={groupBy}
            onChange={e => { setGroupBy(e.target.value); localStorage.setItem('pharmbilling_groupby', e.target.value); }}>
            <option value="patient">Group by Patient</option>
            <option value="date">Group by Date</option>
            <option value="status">Group by Status</option>
          </select>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-ink-soft)', whiteSpace:'nowrap' }}>
          Show
          <select className="select" style={{ width:72, fontWeight:600, padding:'8px 6px' }} value={limit}
            onChange={e => setLimit(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          per page
        </div>
      </div>

      {/* Status filter tabs — same style as Treatment Billing */}
      <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch', marginBottom:18 }}>
        <div style={{ display:'flex', gap:4, background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:10, padding:4, width:'fit-content', minWidth:'max-content' }}>
          {PHARM_STATUS_OPTIONS.map(opt => {
            const count = opt.key === 'all' ? transactions.length
              : transactions.filter(t => statusOf(t.patientUID) === opt.key).length;
            const active = statusFilter === opt.key;
            return (
              <button key={opt.key} onClick={() => setStatusFilter(opt.key)}
                className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}>
                {opt.label}
                <span style={{ fontSize:11, fontWeight:700, background:active?'rgba(255,255,255,0.25)':'var(--color-primary-soft)', color:active?'white':'var(--color-primary)', borderRadius:100, padding:'1px 7px', marginLeft:6 }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display:'grid', gap:10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:80, borderRadius:8 }} />)}
        </div>
      ) : afterFilter.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
          <Receipt size={36} style={{ marginBottom:12, opacity:0.5 }} />
          <p style={{ margin:0 }}>No pharmacy billing records{selectedDate!=='all' ? ` for ${selectedDate}` : ''}.</p>
        </div>
      ) : groupBy === 'date' ? (
        <div style={{ display:'grid', gap:16 }}>
          {dateGroups.map(([date, items]) => (
            <div key={date} className="card" style={{ overflow:'hidden' }}>
              <GroupHeader label={date} items={items} />
              <GroupItems items={items} showPatient={true} listMode={listMode} />
            </div>
          ))}
        </div>
      ) : groupBy === 'status' ? (
        <div style={{ display:'grid', gap:16 }}>
          {statusGroups.filter(g=>g.items.length>0).map(g => {
            const accent = g.key==='paid' ? 'var(--color-success)' : g.key==='partial' ? 'var(--color-warning)' : 'var(--color-danger)';
            return (
              <div key={g.key} className="card" style={{ overflow:'hidden' }}>
                <GroupHeader label={g.label} items={g.items} accent={accent} />
                <GroupItems items={g.items} showPatient={true} listMode={listMode} />
              </div>
            );
          })}
        </div>
      ) : listMode === 'compact' ? (
        <div className="card" style={{ overflow:'hidden' }}>
          {pagedPatientGroups.map((g,i) => <CompactPatientRow key={g.uid||g.name} g={g} i={i} shared={sharedPharmProps} />)}
        </div>
      ) : listMode === 'grid' ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
          {pagedPatientGroups.map((g, i) => <GridPatientCard key={g.uid||g.name} g={g} shared={sharedPharmProps} i={i} />)}
        </div>
      ) : (
        <div style={{ display:'grid', gap:14 }}>
          {pagedPatientGroups.map(g => <PharmPatientCard key={g.uid||g.name} g={g} shared={sharedPharmProps} />)}
        </div>
      )}

      <PaginationBar page={page} setPage={setPage} usedPages={usedPages} usedTotal={usedTotal} limit={limit} />

      {/* Pharmacy-only bill print modal — clinic header, patient details, pharmacy items only */}
      {printPharmacyBillPatient && (
        <PharmacyBillPrintModal
          patient={printPharmacyBillPatient}
          onClose={() => setPrintPharmacyBillPatient(null)}
        />
      )}
    </div>
  );
}

// ── Top-level Pharmacy Billing display components ──────────────────────────
// These MUST be top-level (not nested inside PharmacyBillingView) — a nested
// function declaration gets recreated on every parent render, which makes
// React treat it as a brand-new component type and remount the whole
// subtree. That was silently breaking every input inside Manage/Correct:
// typing a single character caused the parent to re-render, which recreated
// these components, which remounted the input and dropped focus (dismissing
// the mobile keyboard) — looking exactly like "the box closes" after one
// keystroke. Keeping them top-level (like BillingPatientCard/BillingCardGroup
// already are, on the Treatment Billing side) keeps their identity stable
// across renders so typing works normally.

function fmtP(n) { return Math.round(n||0).toLocaleString('en-IN'); }

function TxnRow({ t, i, showPatient }) {
  const amount = (t.quantity||0) * (t.sellingPrice||0);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
      borderTop: i===0?'none':'1px solid var(--color-border)',
      background: i%2===0 ? 'transparent' : 'var(--color-surface)' }}>
      <div style={{ flex:1, minWidth:0 }}>
        <span style={{ fontWeight:700, fontSize:13 }}>{t.itemName}</span>
        {showPatient && t.patientName && (
          <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>
            {t.patientName}{t.patientUID ? ' · '+t.patientUID : ''}
          </span>
        )}
        <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{t.date?.slice(0,10)}</span>
        {t.notes && <span style={{ marginLeft:6, fontSize:11, color:'var(--color-ink-faint)', fontStyle:'italic' }}>{t.notes}</span>}
      </div>
      <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>×{t.quantity}</span>
      <span style={{ fontSize:13, fontWeight:700 }}>₹{fmtP(amount)}</span>
    </div>
  );
}

// ── patient card (list view, group-by-patient) ────────────────────────────
function PharmPatientCard({ g, shared }) {
  const { isAdmin, billingRecords, patientPhones, managingUid, correctingUid, setPrintPharmacyBillPatient,
    startManage, startCorrection, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment,
    correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection,
    saveNotes, recordPayment } = shared;
  const total   = g.items.reduce((s,t) => s+(t.quantity||0)*(t.sellingPrice||0), 0);
  const rec     = billingRecords[g.uid];
  const owed    = rec?.owed ?? total; // net of discount once loaded; falls back to gross before first fetch
  const paidAmt = rec?.amountPaid || 0;
  const pending = Math.max(owed - paidAmt, 0);
  const phone   = patientPhones[g.uid] || '';
  const lastDate = g.items.reduce((max,t) => (t.date||'') > max ? t.date : max, '');
  const waMsg   = buildPharmWAMessage(g, fmtP);
  const isManaging   = managingUid   === g.uid;
  const isCorrecting = correctingUid === g.uid;
  return (
    <div className="card" style={{ overflow:'hidden' }}>
      {/* Patient header */}
      <div style={{ padding:'12px 16px', background:'var(--color-surface)', borderBottom:'1px solid var(--color-border)',
        display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:800, fontSize:15 }}>
            {g.name}
            {g.uid && <span style={{ marginLeft:10, fontSize:11, fontWeight:400, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</span>}
            {phone && <span style={{ marginLeft:10, fontSize:12, fontWeight:400, color:'var(--color-ink-faint)' }}>📱 {phone}</span>}
            {lastDate && <span style={{ marginLeft:10, fontSize:12, fontWeight:400, color:'var(--color-ink-faint)' }}>📅 {lastDate}</span>}
          </div>
          <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>
            {g.items.length} item{g.items.length!==1?'s':''}
            {paidAmt > 0  && <span style={{ marginLeft:12, color:'var(--color-success)' }}>✓ Paid ₹{fmtP(paidAmt)}</span>}
            {pending > 0 && <span style={{ marginLeft:12, color:'var(--color-warning)' }}>⏳ Pending ₹{fmtP(pending)}</span>}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>TOTAL</div>
            <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)' }}>₹{fmtP(owed)}</div>
          </div>
          <WhatsAppButton patient={{ name: g.name, phone, _billMessage: waMsg }} size="xs" billMode />
          <button className="btn btn-outline btn-sm" onClick={() => setPrintPharmacyBillPatient({ name: g.name, patientUID: g.uid })}>
            <Printer size={13}/> Print
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => startManage(g)}>{isManaging ? 'Close' : 'Manage'}</button>
          {isAdmin && <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => startCorrection(g)}><ShieldAlert size={13}/>{isCorrecting ? 'Close' : 'Correct'}</button>}
        </div>
      </div>
      {isManaging && (
        <div style={{ padding:'0 16px 16px' }}>
          <PharmManagePanel g={g} grossTotal={total} record={rec} billingForm={billingForm} setBillingForm={setBillingForm}
            paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment}
            onSaveNotes={() => saveNotes(g)} onRecordPayment={() => recordPayment(g)}
            />
        </div>
      )}
      {isCorrecting && isAdmin && (
        <div style={{ padding:'0 16px 16px' }}>
          <PharmCorrectionPanel grossTotal={total} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm}
            correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting}
            onApply={() => applyCorrection(g)} />
        </div>
      )}
      {/* Item rows */}
      <div>
        {g.items.map((t,i) => <TxnRow key={t._id} t={t} i={i} showPatient={false} />)}
      </div>
    </div>
  );
}

// ── compact patient row ────────────────────────────────────────────────────
function CompactPatientRow({ g, i, shared }) {
  const { isAdmin, billingRecords, patientPhones, managingUid, correctingUid, setPrintPharmacyBillPatient,
    startManage, startCorrection, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment,
    correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection,
    saveNotes, recordPayment } = shared;
  const total   = g.items.reduce((s,t) => s+(t.quantity||0)*(t.sellingPrice||0), 0);
  const rec     = billingRecords[g.uid];
  const owed    = rec?.owed ?? total;
  const paidAmt = rec?.amountPaid || 0;
  const pending = Math.max(owed - paidAmt, 0);
  const phone   = patientPhones[g.uid] || '';
  const lastDate = g.items.reduce((max,t) => (t.date||'') > max ? t.date : max, '');
  const waMsg   = buildPharmWAMessage(g, fmtP);
  const isManaging   = managingUid   === g.uid;
  const isCorrecting = correctingUid === g.uid;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
        borderTop: i===0?'none':'1px solid var(--color-border)',
        background: i%2===0?'transparent':'var(--color-surface)' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <span style={{ fontWeight:700, fontSize:13 }}>{g.name}</span>
          {g.uid && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</span>}
          {phone && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>📱 {phone}</span>}
          {lastDate && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>📅 {lastDate}</span>}
          <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{g.items.length} item{g.items.length!==1?'s':''}</span>
        </div>
        {pending>0 && <span style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmtP(pending)} pending</span>}
        <span style={{ fontSize:13, fontWeight:800, color:'var(--color-primary)' }}>₹{fmtP(owed)}</span>
        <WhatsAppButton patient={{ name: g.name, phone, _billMessage: waMsg }} size="xs" billMode />
        <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }}
          onClick={() => setPrintPharmacyBillPatient({ name: g.name, patientUID: g.uid })}>
          <Printer size={12}/>
        </button>
        <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => startManage(g)}>{isManaging ? 'Close' : 'Manage'}</button>
        {isAdmin && <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => startCorrection(g)}>{isCorrecting ? 'Close' : 'Correct'}</button>}
      </div>
      {isManaging && (
        <div style={{ padding:'0 14px 12px' }}>
          <PharmManagePanel g={g} grossTotal={total} record={rec} billingForm={billingForm} setBillingForm={setBillingForm}
            paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment}
            onSaveNotes={() => saveNotes(g)} onRecordPayment={() => recordPayment(g)}
            />
        </div>
      )}
      {isCorrecting && isAdmin && (
        <div style={{ padding:'0 14px 12px' }}>
          <PharmCorrectionPanel grossTotal={total} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm}
            correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting}
            onApply={() => applyCorrection(g)} />
        </div>
      )}
    </div>
  );
}

// ── grid patient card ──────────────────────────────────────────────────────
function GridPatientCard({ g, shared, i = 0 }) {
  const { isAdmin, billingRecords, patientPhones, managingUid, correctingUid, setPrintPharmacyBillPatient,
    startManage, startCorrection, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment,
    correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection,
    saveNotes, recordPayment } = shared;
  const total   = g.items.reduce((s,t) => s+(t.quantity||0)*(t.sellingPrice||0), 0);
  const rec     = billingRecords[g.uid];
  const owed    = rec?.owed ?? total;
  const paidAmt = rec?.amountPaid || 0;
  const pending = Math.max(owed - paidAmt, 0);
  const phone   = patientPhones[g.uid] || '';
  const lastDate = g.items.reduce((max,t) => (t.date||'') > max ? t.date : max, '');
  const waMsg   = buildPharmWAMessage(g, fmtP);
  const isManaging   = managingUid   === g.uid;
  const isCorrecting = correctingUid === g.uid;
  return (
    <div className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
      <div className="card tilt3d-card" style={{ padding:14, display:'flex', flexDirection:'column', gap:8 }}>
        <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:6 }}>
          {g.name}
        </div>
        {g.uid && <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</div>}
        {phone && <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>📱 {phone}</div>}
        {lastDate && <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>📅 {lastDate}</div>}
        <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>{g.items.length} item{g.items.length!==1?'s':''}</div>
        {pending>0 && <div style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmtP(pending)} pending</div>}
        <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center', marginTop:'auto', paddingTop:6, borderTop:'1px solid var(--color-border)' }}>
          <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)', flex:1 }}>₹{fmtP(owed)}</div>
          <WhatsAppButton patient={{ name: g.name, phone, _billMessage: waMsg }} size="xs" billMode />
          <button className="btn btn-outline btn-sm" style={{ padding:'4px 8px', fontSize:11 }}
            onClick={() => setPrintPharmacyBillPatient({ name: g.name, patientUID: g.uid })}>
            <Printer size={12}/>
          </button>
          <button className="btn btn-outline btn-sm" style={{ padding:'4px 8px', fontSize:11 }} onClick={() => startManage(g)}>{isManaging ? 'Close' : 'Manage'}</button>
          {isAdmin && <button className="btn btn-sm" style={{ padding:'4px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => startCorrection(g)}>{isCorrecting ? 'Close' : 'Correct'}</button>}
        </div>
      </div>
      {isManaging && (
        <div style={{ marginTop:4 }}>
          <PharmManagePanel g={g} grossTotal={total} record={rec} billingForm={billingForm} setBillingForm={setBillingForm}
            paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment}
            onSaveNotes={() => saveNotes(g)} onRecordPayment={() => recordPayment(g)}
            />
        </div>
      )}
      {isCorrecting && isAdmin && (
        <div style={{ marginTop:4 }}>
          <PharmCorrectionPanel grossTotal={total} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm}
            correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting}
            onApply={() => applyCorrection(g)} />
        </div>
      )}
    </div>
  );
}

// ── group header (date and status groups) ─────────────────────────────────
function GroupHeader({ label, items, accent }) {
  const total = items.reduce((s,t) => s+(t.quantity||0)*(t.sellingPrice||0), 0);
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10,
      padding:'10px 14px', background:'var(--color-surface)',
      borderBottom:'1px solid var(--color-border)', borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <Layers size={13} color="var(--color-ink-faint)" />
        <span style={{ fontWeight:700, fontSize:13, color: accent||'inherit' }}>{label}</span>
        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {items.length} item{items.length!==1?'s':''}</span>
      </div>
      <div style={{ textAlign:'right' }}>
        <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>TOTAL</div>
        <div style={{ fontWeight:800, fontSize:14, color:'var(--color-ink)' }}>₹{fmtP(total)}</div>
      </div>
    </div>
  );
}

// ── render group items with listMode ──────────────────────────────────────
function GroupItems({ items, showPatient, listMode }) {
  if (listMode === 'compact') {
    return (
      <div>
        {items.map((t,i) => <TxnRow key={t._id} t={t} i={i} showPatient={showPatient} />)}
      </div>
    );
  }
  if (listMode === 'grid') {
    return (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10, padding:12 }}>
        {items.map(t => {
          const amount = (t.quantity||0)*(t.sellingPrice||0);
          return (
            <div key={t._id} className="tilt3d-scene">
            <div className="tilt3d-card" style={{ padding:12, border:'1px solid var(--color-border)', borderRadius:8, display:'flex', flexDirection:'column', gap:6 }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{t.itemName}</div>
              {showPatient && <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.patientName}</div>}
              <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.date?.slice(0,10)}</div>
              <div style={{ marginTop:'auto', paddingTop:6, borderTop:'1px solid var(--color-border)' }}>
                <span style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)' }}>₹{fmtP(amount)}</span>
              </div>
            </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div>
      {items.map((t,i) => (
        <div key={t._id} style={{ borderTop: i>0 ? '1px solid var(--color-border)' : 'none' }}>
          <TxnRow t={t} i={0} showPatient={showPatient} />
        </div>
      ))}
    </div>
  );
}

// ── pagination control ─────────────────────────────────────────────────────
function PaginationBar({ page, setPage, usedPages, usedTotal, limit }) {
  if (usedPages <= 1) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:20, flexWrap:'wrap', gap:12 }}>
      <div style={{ fontSize:13, color:'var(--color-ink-soft)' }}>
        Showing <strong>{(page-1)*limit+1}</strong>–<strong>{Math.min(page*limit, usedTotal)}</strong> of <strong>{usedTotal}</strong>
      </div>
      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
        <button className="btn btn-outline btn-sm" onClick={() => setPage(1)} disabled={page<=1}><ChevronsLeft size={14}/></button>
        <button className="btn btn-outline btn-sm" onClick={() => setPage(p=>p-1)} disabled={page<=1}><ChevronLeft size={14}/></button>
        <span style={{ fontSize:13, fontWeight:600, padding:'0 8px' }}>Page {page} / {usedPages}</span>
        <button className="btn btn-outline btn-sm" onClick={() => setPage(p=>p+1)} disabled={page>=usedPages}><ChevronRight size={14}/></button>
        <button className="btn btn-outline btn-sm" onClick={() => setPage(usedPages)} disabled={page>=usedPages}><ChevronsRight size={14}/></button>
      </div>
    </div>
  );
}


// ── Pharmacy Manage panel — record payments against the computed pharmacy
// total for a patient. Notes + payments only; the owed amount is always
// derived from PharmacyTransaction records and is not directly editable
// here (use Correct, admin-only, to override amount paid).
function PharmManagePanel({ g, grossTotal, record, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, onSaveNotes, onRecordPayment }) {
  const bf = (field) => (e) => setBillingForm((f) => ({ ...f, [field]: e.target.value }));
  const pf = (field) => (e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }));
  const amountPaid = record?.amountPaid || 0;
  const discount   = record?.discount || 0;
  const owed       = record?.owed ?? Math.max(grossTotal - discount, 0);
  const pending    = Math.max(owed - amountPaid, 0);
  const status     = owed === 0 ? 'unpaid' : amountPaid >= owed ? 'paid' : amountPaid > 0 ? 'partial' : 'unpaid';

  // Old Balance breakdown — own local state (not read from record directly)
  // so a payment applied here updates instantly, without waiting on the
  // parent's socket-driven refresh. Same pattern as Treatment Billing's
  // OldBalancePanel.
  const [oldBalance, setOldBalance] = React.useState(record?.oldBalance || 0);
  const [oldBalanceBreakdown, setOldBalanceBreakdown] = React.useState(record?.oldBalanceBreakdown || []);
  React.useEffect(() => {
    setOldBalance(record?.oldBalance || 0);
    setOldBalanceBreakdown(record?.oldBalanceBreakdown || []);
  }, [record?.oldBalance, record?.oldBalanceBreakdown]);

  function handleOldBalanceSettled(result) {
    setOldBalance(Math.max((result.billing?.oldBalance) ?? 0, 0));
  }

  return (
    <div style={{ paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
      {/* Old Balance — what this patient already owed from purchases BEFORE
          today, kept visually separate from today's new charge. Pharmacy
          tracks discount/payments at the whole-account level, so this is
          apportioned using a FIFO convention (oldest charges settled
          first) — see splitOldAndCurrentPharmacyBalance in billingMath.js. */}
      {oldBalance > 0 && (
        <PharmOldBalancePanel
          patientUID={g.uid} patientName={g.name} oldBalance={oldBalance} breakdown={oldBalanceBreakdown}
          onSettled={handleOldBalanceSettled}
        />
      )}
      <div style={{ display:'flex', gap:18, flexWrap:'wrap', alignItems:'center', marginBottom:14, fontSize:13 }}>
        <div><span style={{ color:'var(--color-ink-faint)' }}>Items Total: </span><strong>₹{fmt(grossTotal)}</strong></div>
        {discount > 0 && <div><span style={{ color:'var(--color-ink-faint)' }}>Discount: </span><strong style={{ color:'var(--color-warning)' }}>−₹{fmt(discount)}</strong></div>}
        <div><span style={{ color:'var(--color-ink-faint)' }}>Owed: </span><strong>₹{fmt(owed)}</strong></div>
        <div><span style={{ color:'var(--color-ink-faint)' }}>Paid: </span><strong style={{ color:'var(--color-success)' }}>₹{fmt(amountPaid)}</strong></div>
        <div><span style={{ color:'var(--color-ink-faint)' }}>Pending: </span><strong style={{ color: pending>0 ? 'var(--color-warning)' : 'var(--color-ink-faint)' }}>₹{fmt(pending)}</strong></div>
        <PaymentBadge status={status} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12, marginBottom:4 }}>
        <F label="Discount (₹)"><input className="input" type="number" min="0" value={billingForm.discount} onChange={bf('discount')} /></F>
        <F label="Notes"><input className="input" value={billingForm.notes} onChange={bf('notes')} placeholder="Reference, notes…" /></F>
      </div>
      <button className="btn btn-primary btn-sm" onClick={onSaveNotes} disabled={saving}>
        <Save size={14} />
        {saving ? 'Saving…' : 'Save'}
      </button>

      {/* Record a Payment section */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Record a Payment</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Amount (₹)" style={{ width: 120, marginBottom: 0 }}>
            <input className="input" type="number" min="0" value={paymentForm.amount} onChange={pf('amount')} placeholder="0" />
          </F>
          <F label="Method" style={{ width: 130, marginBottom: 0 }}>
            <select className="select" value={paymentForm.method} onChange={pf('method')}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </F>
          <F label="Date" style={{ width: 150, marginBottom: 0 }}>
            <input className="input" type="date" max={todayISO()} value={paymentForm.date || todayISO()} onChange={pf('date')} />
          </F>
          <F label="Note" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={paymentForm.note} onChange={pf('note')} placeholder="Receipt #…" />
          </F>
          <button
            className="btn btn-primary btn-sm"
            onClick={onRecordPayment}
            disabled={recordingPayment}
            style={{ alignSelf: 'flex-end', marginBottom: 0 }}
          >
            <Plus size={14} />
            {recordingPayment ? 'Recording…' : 'Add Payment'}
          </button>
        </div>
        <PharmPaymentHistory history={record?.paymentHistory} g={g} />
      </div>
    </div>
  );
}

// Pharmacy's Old Balance breakdown + payment — shows exactly which past
// purchase date(s) a patient's carried-forward balance comes from, and
// lets it be paid off directly (mirrors Treatment Billing's
// OldBalancePanel). Since Pharmacy tracks discount/payments at the
// whole-account level rather than per-visit, the breakdown below is a
// list of past PURCHASE DATES (not separate payable documents) — paying
// "old balance" here is the same underlying amountPaid field as any other
// payment, just tagged and reported so it's clear how much actually
// settled the old charges vs spilled into today's/other balance.
function PharmOldBalancePanel({ patientUID, patientName, oldBalance, breakdown, onSettled }) {
  const { showToast } = useToast();
  const [showPayForm, setShowPayForm] = React.useState(false);
  const [form, setForm] = React.useState({ amount: '', method: 'cash', note: '', date: todayISO() });
  const [paying, setPaying] = React.useState(false);

  async function submitPayment() {
    if (!form.amount || Number(form.amount) <= 0) { showToast('Enter an amount greater than 0.', 'error'); return; }
    setPaying(true);
    try {
      const res = await api.put(`/pharmacy/billing/${patientUID}/old-balance/payment`, {
        patientName, amount: Number(form.amount), method: form.method, note: form.note, paidAt: form.date,
      });
      onSettled(res.data);
      const { appliedToOldBalance, appliedToOtherBalance, excessOverpayment } = res.data;
      let msg = `₹${fmt(appliedToOldBalance)} applied to Old Balance.`;
      if (appliedToOtherBalance > 0) msg += ` ₹${fmt(appliedToOtherBalance)} went toward the rest of the balance.`;
      if (excessOverpayment > 0) msg += ` ₹${fmt(excessOverpayment)} was more than was owed overall and wasn't applied.`;
      showToast(msg, 'success');
      setForm({ amount: '', method: 'cash', note: '', date: todayISO() });
      setShowPayForm(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not record this payment.', 'error');
    } finally {
      setPaying(false);
    }
  }

  return (
    <div style={{
      marginBottom: 14, padding: '10px 14px', borderRadius: 8,
      background: 'var(--color-warning-soft)', border: '1px solid var(--color-warning)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <IndianRupee size={16} color="var(--color-warning)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Old Balance (from before today)
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-ink)' }}>
            ₹{fmt(oldBalance)} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-ink-faint)' }}>still owed from earlier purchases</span>
          </div>
        </div>
        <button className="btn btn-sm" style={{ background: 'var(--color-warning)', color: 'white' }} onClick={() => setShowPayForm((v) => !v)}>
          {showPayForm ? 'Close' : 'Pay Old Balance'}
        </button>
      </div>

      {/* Breakdown — which purchase date(s) this comes from. Gross purchase
          totals per date, not a per-date pending figure — Pharmacy's
          discount/payments apply at the account level, not per-date, so
          only the combined Old Balance total above is the true pending
          figure; this list is for visibility into what it's made up of. */}
      {breakdown.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
          {breakdown.map((b) => (
            <div key={b.date} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-ink-soft)' }}>
              <span>{b.date}</span>
              <span style={{ fontWeight: 700 }}>₹{fmt(b.gross)}</span>
            </div>
          ))}
        </div>
      )}

      {showPayForm && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-warning)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Amount (₹)" style={{ width: 120, marginBottom: 0 }}>
            <input className="input" type="number" min="0" max={oldBalance} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0" />
          </F>
          <F label="Method" style={{ width: 130, marginBottom: 0 }}>
            <select className="select" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </F>
          <F label="Date" style={{ width: 150, marginBottom: 0 }}>
            <input className="input" type="date" max={todayISO()} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </F>
          <F label="Note" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Receipt #…" />
          </F>
          <button className="btn btn-sm" style={{ background: 'var(--color-warning)', color: 'white', marginBottom: 0 }} onClick={submitPayment} disabled={paying}>
            {paying ? 'Recording…' : 'Record Payment'}
          </button>
        </div>
      )}
    </div>
  );
}

// Admin-only correction panel — overrides amount paid + discount directly, reason required and logged
function PharmCorrectionPanel({ grossTotal, correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, onApply }) {
  const cf = (field) => (e) => setCorrectionForm((f) => ({ ...f, [field]: e.target.value }));
  const amountPaid = Number(correctionForm.amountPaid) || 0;
  const discount   = Number(correctionForm.discount) || 0;
  const owed       = Math.max(grossTotal - discount, 0);
  const status = owed === 0 ? 'unpaid' : amountPaid >= owed ? 'paid' : amountPaid > 0 ? 'partial' : 'unpaid';

  return (
    <div style={{ paddingTop: 16, borderTop: '2px solid var(--color-warning)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <ShieldAlert size={16} color="var(--color-warning)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)' }}>Admin Pharmacy Billing Correction</span>
        <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>— Use only to fix entries made in error. All changes are logged.</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <F label="Items Total (computed from sales)">
          <input className="input" value={`₹${fmt(grossTotal)}`} readOnly style={{ background:'var(--color-surface)', cursor:'not-allowed', opacity:0.7 }} />
        </F>
        <F label="Discount (₹)"><input className="input" type="number" min="0" value={correctionForm.discount} onChange={cf('discount')} /></F>
        <F label="Amount Paid (₹)"><input className="input" type="number" value={correctionForm.amountPaid} onChange={cf('amountPaid')} /></F>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-soft)' }}>
        <span>→ Owed ₹{fmt(owed)}, status will be auto-set to</span>
        <PaymentBadge status={status} />
      </div>
      <F label="Notes">
        <input className="input" value={correctionForm.notes || ''} onChange={cf('notes')} />
      </F>
      <F label="Reason for correction (required)">
        <input
          className="input"
          value={correctionReason}
          onChange={(e) => setCorrectionReason(e.target.value)}
          placeholder="e.g. Wrong payment entered — correcting ₹500 to ₹300"
          style={{ borderColor: !correctionReason ? 'var(--color-warning)' : undefined }}
        />
      </F>
      <button
        className="btn btn-sm"
        style={{ background: 'var(--color-warning)', color: 'white' }}
        onClick={onApply}
        disabled={correcting || !correctionReason.trim()}
      >
        <ShieldAlert size={14} />
        {correcting ? 'Applying…' : 'Apply Correction'}
      </button>
    </div>
  );
}

// Payment history for a patient's pharmacy billing record
function PharmPaymentHistory({ history, g }) {
  const [localHistory, setLocalHistory] = React.useState(history || []);
  React.useEffect(() => { setLocalHistory(history || []); }, [history]);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const { showToast } = useToast();

  if (!localHistory?.length) return null;

  function startEdit(entry) {
    setEditingId(entry._id);
    setEditForm({
      amount: entry.amount || 0,
      method: entry.method || 'cash',
      note:   entry.note || '',
      date:   entry.paidAt ? toLocalISODate(entry.paidAt) : todayISO(),
    });
  }

  async function saveEdit(entryId) {
    setSaving(true);
    try {
      const res = await api.put(`/pharmacy/billing/${g.uid}/payment/${entryId}`, {
        amount: Number(editForm.amount) || 0,
        method: editForm.method,
        note:   editForm.note,
        paidAt: editForm.date,
      });
      setLocalHistory(res.data.billing?.paymentHistory || localHistory);
      showToast('Payment updated.', 'success');
      setEditingId(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update this payment.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
        <History size={13} /> Payment History
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {[...localHistory].reverse().map((entry) => (
          <div
            key={entry._id}
            style={{
              padding: '8px 10px',
              background: entry.amount === 0 ? 'var(--color-warning-soft)' : 'var(--color-bg)',
              borderRadius: 6,
            }}
          >
            {editingId === entry._id ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <F label="Amount (₹)" style={{ width: 100, marginBottom: 0 }}>
                  <input className="input" type="number" min="0" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} />
                </F>
                <F label="Method" style={{ width: 110, marginBottom: 0 }}>
                  <select className="select" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.method} onChange={e => setEditForm(f => ({ ...f, method: e.target.value }))}>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="insurance">Insurance</option>
                    <option value="other">Other</option>
                  </select>
                </F>
                <F label="Date" style={{ width: 140, marginBottom: 0 }}>
                  <input className="input" type="date" style={{ fontSize: 12, padding: '4px 8px' }} max={todayISO()}
                    value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                </F>
                <F label="Note" style={{ flex: 1, minWidth: 100, marginBottom: 0 }}>
                  <input className="input" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
                </F>
                <button className="btn btn-primary btn-sm" style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={() => saveEdit(entry._id)} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-outline btn-sm" style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ flex: 1 }}>
                  {entry.amount > 0
                    ? <strong>₹{entry.amount.toLocaleString('en-IN')}</strong>
                    : <span style={{ color: 'var(--color-warning)', fontWeight: 700 }}>⚙ Correction</span>}
                  {entry.amount > 0 && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.method}</span>}
                  {entry.note && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.note}</span>}
                </span>
                <div style={{ textAlign: 'right', color: 'var(--color-ink-faint)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <Clock size={11} />
                      {fmtDateTime(entry.paidAt)}
                    </div>
                    {entry.recordedBy && <div style={{ fontSize: 11 }}>by {entry.recordedBy}</div>}
                  </div>
                  {entry.amount > 0 && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }} title="Edit this payment"
                      onClick={() => startEdit(entry)}>
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


export default function BillingPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.isAdmin === true;
  const [searchParams] = useSearchParams();

  const [patients, setPatients] = useState([]);
  const [totals, setTotals] = useState({ billed: 0, collected: 0, balance: 0 });
  const [loading, setLoading] = useState(true);

  // Remember the last selected date until the user manually changes it —
  // was resetting to "all" on every visit/reload before.
  const TREAT_BILLING_DATE_KEY = 'treatbilling_selectedDate';
  const TREAT_BILLING_DATES_KEY = 'treatbilling_selectedDates';
  const [selectedDate, setSelectedDateRaw] = useState(() => localStorage.getItem(TREAT_BILLING_DATE_KEY) || 'all');
  const [selectedDates, setSelectedDatesRaw] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TREAT_BILLING_DATES_KEY) || 'null');
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* ignore malformed storage */ }
    return new Set();
  });
  function setSelectedDate(d) {
    setSelectedDateRaw(d);
    localStorage.setItem(TREAT_BILLING_DATE_KEY, d);
    // A single-date pick (via onChange, not onMultiChange) replaces any
    // multi-selection rather than adding to it.
    const newDates = d === 'all' ? new Set() : new Set([d]);
    setSelectedDatesRaw(newDates);
    localStorage.setItem(TREAT_BILLING_DATES_KEY, JSON.stringify([...newDates]));
  }
  function setSelectedDates(set) {
    setSelectedDatesRaw(set);
    localStorage.setItem(TREAT_BILLING_DATES_KEY, JSON.stringify([...set]));
    const d = set.size === 1 ? [...set][0] : 'all';
    setSelectedDateRaw(d);
    localStorage.setItem(TREAT_BILLING_DATE_KEY, d);
  }
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableDates, setAvailableDates] = useState(new Set());
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') || '');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);
  const [sortKey, setSortKey] = useState(() => localStorage.getItem('billing_sort') || 'tokenDate-desc');
  const [showStats, setShowStats] = useState(() => localStorage.getItem('billing_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('billing_showStats', !v); return !v; });
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('billing_groupby') || 'patient');
  const [billingTab, setBillingTab] = useState(() => (searchParams.get('tab') === 'pharmacy' ? 'pharmacy' : 'treatment')); // 'treatment' | 'pharmacy'
  const [listMode, setListMode] = useViewMode('billing_view', 'list');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [editingId, setEditingId] = useState(null);
  const [printBillPatient, setPrintBillPatient] = useState(null);
  const [billingForm, setBillingForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', note: '', date: todayISO() });
  const [saving, setSaving] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);

  const [correctionId, setCorrectionId] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({});
  const [correctionReason, setCorrectionReason] = useState('');
  const [correcting, setCorrecting] = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: debouncedSearch, page, limit };
      if (selectedDates.size > 1) params.dates = [...selectedDates].join(',');
      else params.date = selectedDate;
      const res = await api.get('/patients/billing/daily', { params });
      setPatients(res.data.patients);
      setTotals(res.data.totals);
      setTotalCount(res.data.total);
      if (res.data.availableDates) setAvailableDates(new Set(res.data.availableDates));
    } catch (err) {
      showToast('Could not load billing records.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedDates, debouncedSearch, page, limit, showToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [selectedDate, selectedDates, debouncedSearch, limit, statusFilter]);

  useSocketEvent('patient:billing-updated', (payload) => {
    if (!payload?.patient) return;
    setPatients((prev) => prev.map((p) =>
      p._id === payload.patient._id ? payload.patient : p
    ));
    load();
  });
  useSocketEvent('queue:reset', load);
  // Cancelling a patient's slot changes their status to 'cancelled', which
  // now excludes them from Treatment Billing entirely (see getDailyBilling) —
  // refresh live so the removal shows up immediately, not just on reload.
  useSocketEvent('patient:status-changed', load);

  // ── helpers ────────────────────────────────────────────────────────────────
  function startEdit(patient) {
    setEditingId(patient._id);
    setCorrectionId(null);
    // Note: amountPaid is intentionally NOT in the billing form here.
    // Changing bill details (amount, discount, tax) should NOT overwrite
    // what has already been paid — that's only touched via "Record a Payment"
    // or admin correction. This prevents payment history from being lost.
    setBillingForm({
      invoiceNumber: patient.billing?.invoiceNumber || '',
      amount:        patient.billing?.amount || 0,
      discount:      patient.billing?.discount || 0,
      taxPercent:    patient.billing?.taxPercent || 0,
      dueDate:       patient.billing?.dueDate ? patient.billing.dueDate.slice(0, 10) : '',
      notes:         patient.billing?.notes || '',
    });
    setPaymentForm({ amount: '', method: 'cash', note: '', date: todayISO() });
  }

  function startCorrection(patient) {
    setCorrectionId(patient._id);
    setEditingId(null);
    setCorrectionForm({
      invoiceNumber:  patient.billing?.invoiceNumber || '',
      amount:         patient.billing?.amount || 0,
      discount:       patient.billing?.discount || 0,
      taxPercent:     patient.billing?.taxPercent || 0,
      amountPaid:     patient.billing?.amountPaid || 0,
      paymentStatus:  patient.billing?.paymentStatus || 'unpaid',
      paymentMethod:  patient.billing?.paymentMethod || 'none',
      dueDate:        patient.billing?.dueDate ? patient.billing.dueDate.slice(0, 10) : '',
      notes:          patient.billing?.notes || '',
    });
    setCorrectionReason('');
  }

  // "Save Billing" — only updates invoice, bill amount, discount, tax, due date,
  // and notes. Does NOT touch amountPaid, paymentMethod, or paymentStatus.
  // Payment status is re-derived server-side from the new net total vs. what
  // has already been paid, so the status badge always reflects reality.
  async function saveBilling(patientId, currentPatient) {
    setSaving(true);
    try {
      const currentPaid  = currentPatient.billing?.amountPaid || 0;
      const currentNet   = netTotal({
        amount:     Number(billingForm.amount),
        discount:   Number(billingForm.discount),
        taxPercent: Number(billingForm.taxPercent),
      });
      const autoStatus = deriveStatus(currentNet, currentPaid);

      const res = await api.put(`/patients/${patientId}/billing`, {
        invoiceNumber: billingForm.invoiceNumber,
        amount:        Number(billingForm.amount),
        discount:      Number(billingForm.discount),
        taxPercent:    Number(billingForm.taxPercent),
        dueDate:       billingForm.dueDate || null,
        notes:         billingForm.notes,
        // Auto-derived status so the badge updates correctly after bill changes
        paymentStatus: autoStatus,
        // amountPaid is deliberately omitted — previous payments are preserved
      });
      showToast('Billing details saved.', 'success');
      setPatients((prev) => prev.map((p) => (p._id === patientId ? res.data.patient : p)));
      setEditingId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save billing.', 'error');
    } finally {
      setSaving(false);
    }
  }

  // "Record a Payment" — adds to the running paid total and appends a
  // history entry. Payment status is auto-derived from new paid vs. net total.
  async function recordPayment(patient) {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      showToast('Enter a payment amount greater than zero.', 'error');
      return;
    }
    setRecordingPayment(true);
    try {
      const currentPaid  = patient.billing?.amountPaid || 0;
      const newAmountPaid = currentPaid + Number(paymentForm.amount);
      const total        = netTotal(patient.billing);
      const newStatus    = deriveStatus(total, newAmountPaid);

      const res = await api.put(`/patients/${patient._id}/billing`, {
        amountPaid:    newAmountPaid,
        paymentStatus: newStatus,
        paymentMethod: paymentForm.method,
        newPayment: {
          amount: Number(paymentForm.amount),
          method: paymentForm.method,
          note:   paymentForm.note,
          paidAt: paymentForm.date || undefined,
        },
      });
      showToast(
        `Payment of ₹${fmt(Number(paymentForm.amount))} recorded. Status: ${STATUS_LABELS[newStatus]}.`,
        'success'
      );
      setPatients((prev) => prev.map((p) => (p._id === patient._id ? res.data.patient : p)));
      setPaymentForm({ amount: '', method: 'cash', note: '', date: todayISO() });
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not record payment.', 'error');
    } finally {
      setRecordingPayment(false);
    }
  }

  async function applyCorrection(patientId) {
    if (!correctionReason.trim()) {
      showToast('Please enter a reason for this correction.', 'error');
      return;
    }
    setCorrecting(true);
    try {
      // For admin corrections, auto-derive status from the corrected figures
      const correctedNet   = netTotal({
        amount:     Number(correctionForm.amount),
        discount:   Number(correctionForm.discount),
        taxPercent: Number(correctionForm.taxPercent),
      });
      const correctedStatus = deriveStatus(correctedNet, Number(correctionForm.amountPaid));

      const res = await api.put(`/patients/${patientId}/billing/correction`, {
        ...correctionForm,
        amount:        Number(correctionForm.amount),
        discount:      Number(correctionForm.discount),
        taxPercent:    Number(correctionForm.taxPercent),
        amountPaid:    Number(correctionForm.amountPaid),
        paymentStatus: correctedStatus,
        dueDate:       correctionForm.dueDate || null,
        correctionReason: correctionReason.trim(),
      });
      showToast('Correction applied and logged in payment history.', 'success');
      setPatients((prev) => prev.map((p) => (p._id === patientId ? res.data.patient : p)));
      setCorrectionId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not apply correction.', 'error');
    } finally {
      setCorrecting(false);
    }
  }

  // ── derived display list ───────────────────────────────────────────────────
  const afterStatusFilter = patients.filter((p) => {
    if (!matchesStatusFilter(p.billing?.paymentStatus || 'unpaid', statusFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.patientUID || '').toLowerCase().includes(q)
      );
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // ── Multi-day patient summary ──────────────────────────────────────────────
  const patientSummary = React.useMemo(() => {
    const map = {};
    afterStatusFilter.forEach(p => {
      const uid = p.patientUID;
      if (!map[uid]) map[uid] = { uid, name: p.name, phone: p.phone, age: p.age, gender: p.gender, visits: [] };
      const total   = netTotal(p.billing);
      const paid    = p.billing?.amountPaid || 0;
      const pending = Math.max(total - paid, 0);
      map[uid].visits.push({ id: p._id, date: p.tokenDate, invoice: p.billing?.invoiceNumber, total, paid, pending, status: p.billing?.paymentStatus || 'unpaid' });
    });
    return Object.values(map)
      .map(g => ({
        ...g,
        grandTotal:   roundMoney(g.visits.reduce((s,v) => s + v.total, 0)),
        grandPaid:    roundMoney(g.visits.reduce((s,v) => s + v.paid, 0)),
        grandPending: roundMoney(g.visits.reduce((s,v) => s + v.pending, 0)),
      }))
      .sort((a,b) => {
        const [key, dir] = (sortKey||'tokenDate-desc').split('-');
        const mult = dir === 'asc' ? 1 : -1;
        if (key === 'name') return a.name.localeCompare(b.name) * mult;
        if (key === 'balance') return (a.grandPending - b.grandPending) * mult;
        if (key === 'amount') return (a.grandTotal - b.grandTotal) * mult;
        if (key === 'paymentStatus') {
          const order = { unpaid:0, partial:1, paid:2 };
          const av = order[a.visits[a.visits.length-1]?.status||'unpaid']??0;
          const bv = order[b.visits[b.visits.length-1]?.status||'unpaid']??0;
          return (av - bv) * mult;
        }
        // tokenDate — sort by most recent visit date
        const ad = a.visits.map(v=>v.date).sort().pop()||'';
        const bd = b.visits.map(v=>v.date).sort().pop()||'';
        return ad < bd ? -1*mult : ad > bd ? 1*mult : 0;
      });
  }, [afterStatusFilter, sortKey]);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Billing" subtitle="Manage payments, invoices, and daily revenue">

      {/* Summary cards — admin only, toggleable, Treatment Billing tab only */}
      {isAdmin && billingTab === 'treatment' && (() => {
        const dateLabel = selectedDates.size > 1
          ? `${selectedDates.size} dates selected`
          : selectedDate === 'all' ? '(all time)' : `— ${selectedDate}`;
        return (
        <div style={{ marginBottom: showStats ? 0 : 8 }}>
          <button
            className="btn btn-outline btn-sm"
            style={{ marginBottom: showStats ? 12 : 0, display:'flex', alignItems:'center', gap:6 }}
            onClick={toggleStats}>
            {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
          </button>
          {showStats && <div className="stat-grid">
            <StatCard
              icon={IndianRupee} color="primary"
              label={`Total Billed ${dateLabel}`}
              value={`₹${fmt(totals.billed)}`}
            />
            <StatCard
              icon={IndianRupee} color="success"
              label={`Collected ${dateLabel}`}
              value={`₹${fmt(totals.collected)}`}
            />
            <StatCard
              icon={IndianRupee} color="warning"
              label={`Pending Balance ${dateLabel}`}
              value={`₹${fmt(totals.balance)}`}
            />
          </div>}
        </div>
        );
      })()}

      {/* Tab switcher: Treatment Billing vs Pharmacy Billing */}
      <div style={{ display:'flex', gap:0, marginBottom:16, border:'1px solid var(--color-border)', borderRadius:10, overflow:'hidden', width:'fit-content' }}>
        {[['treatment', Stethoscope, 'Treatment Billing'], ['pharmacy', Pill, 'Pharmacy Billing']].map(([key, Icon, label]) => (
          <button key={key} type="button"
            onClick={() => setBillingTab(key)}
            style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'8px 20px', fontWeight:700, fontSize:13, border:'none', cursor:'pointer',
              background: billingTab === key ? 'var(--color-primary)' : 'var(--color-surface)',
              color: billingTab === key ? '#fff' : 'var(--color-ink-soft)',
              borderRight: key === 'treatment' ? '1px solid var(--color-border)' : 'none',
            }}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Controls row — only for treatment billing */}
      {billingTab === 'treatment' && (<>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <ViewModeSwitcher mode={listMode} onChange={setListMode} />
        <DatePicker
          selectedDate={selectedDate}
          selectedDates={selectedDates}
          onChange={setSelectedDate}
          onMultiChange={setSelectedDates}
          availableDates={availableDates}
          multiSelect
        />

        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input
            className="input"
            style={{ paddingLeft: 32 }}
            placeholder="Search name or patient ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ position: 'relative', minWidth: 200 }}>
          <ArrowUpDown size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', pointerEvents: 'none' }} />
          <select
            className="select"
            style={{ paddingLeft: 30, fontWeight: 600 }}
            value={sortKey}
            onChange={(e) => { setSortKey(e.target.value); localStorage.setItem('billing_sort', e.target.value); }}
          >
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Layers size={14} color="var(--color-ink-faint)" />
          <select
            className="select"
            style={{ minWidth: 150, fontWeight: 600 }}
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="patient">Group by Patient</option>
            <option value="date">Group by Date</option>
            <option value="status">Group by Status</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-ink-soft)', whiteSpace: 'nowrap' }}>
          Show
          <select
            className="select"
            style={{ width: 72, fontWeight: 600, padding: '8px 6px' }}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          per page
        </div>
      </div>

      {/* Status filter tabs — "Unpaid" shown as "Pending" */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 4, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 4, width: 'fit-content', minWidth: 'max-content' }}>
        {FILTER_OPTIONS.map((opt) => {
          const count = opt.key === 'all'
            ? patients.length
            : patients.filter((p) => (p.billing?.paymentStatus || 'unpaid') === opt.key).length;
          const active = statusFilter === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            >
              {opt.label}
              <span style={{ fontSize: 11, fontWeight: 700, background: active ? 'rgba(255,255,255,0.25)' : 'var(--color-primary-soft)', color: active ? 'white' : 'var(--color-primary)', borderRadius: 100, padding: '1px 7px', marginLeft: 6 }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      </div>



      {/* ── Merged view — all patients grouped, every detail included ─── */}
      {loading ? (
        <div style={{ display:'grid', gap:10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:80, borderRadius:8 }} />)}
        </div>
      ) : afterStatusFilter.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
          <Receipt size={36} style={{ marginBottom:12, opacity:0.5 }} />
          <p style={{ margin:0 }}>No billing records found{selectedDate !== 'all' ? ` for ${selectedDate}` : ''}.</p>
        </div>
      ) : groupBy === 'date' ? (
        /* ── Group by Date ── */
        (() => {
          const dateMap = {};
          afterStatusFilter.forEach(p => {
            const d = p.tokenDate || 'No date';
            if (!dateMap[d]) dateMap[d] = [];
            dateMap[d].push(p);
          });
          return (
            <div style={{ display:'grid', gap:16 }}>
              {Object.entries(dateMap).sort(([a],[b]) => b>a?1:-1).map(([date, dps]) => {
                const dtotal = dps.reduce((s,p)=>s+netTotal(p.billing),0);
                const dpaid  = dps.reduce((s,p)=>s+(p.billing?.amountPaid||0),0);
                const dpend  = Math.max(dtotal-dpaid,0);
                return (
                  <div key={date} className="card" style={{ overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, padding:'10px 14px', background:'var(--color-surface)', borderBottom:'1px solid var(--color-border)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Layers size={13} color="var(--color-ink-faint)" />
                        <span style={{ fontWeight:700, fontSize:13 }}>{date}</span>
                        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {dps.length} patient{dps.length>1?'s':''}</span>
                      </div>
                      <div style={{ display:'flex', gap:14 }}>
                        {[['TOTAL',dtotal,'var(--color-ink)'],['PAID',dpaid,'var(--color-success)'],['PENDING',dpend,dpend>0?'var(--color-warning)':'var(--color-ink-faint)']].map(([l,v,c])=>(
                          <div key={l} style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>{l}</div>
                            <div style={{ fontWeight:800, fontSize:14, color:c }}>₹{fmt(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <BillingCardGroup nested patients={dps} sortKey={sortKey} listMode={listMode} isAdmin={isAdmin} editingId={editingId} setEditingId={setEditingId} correctionId={correctionId} setCorrectionId={setCorrectionId} startEdit={startEdit} startCorrection={startCorrection} setPrintBillPatient={setPrintBillPatient} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} saveBilling={saveBilling} recordPayment={recordPayment} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} applyCorrection={applyCorrection} />
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : groupBy === 'status' ? (
        /* ── Group by Payment Status ── */
        (() => {
          const statusOrder = [['unpaid','Pending'],['partial','Partial'],['paid','Paid']];
          return (
            <div style={{ display:'grid', gap:16 }}>
              {statusOrder.map(([key, label]) => {
                const grp = afterStatusFilter.filter(p=>(p.billing?.paymentStatus||'unpaid')===key);
                if (!grp.length) return null;
                const gt = grp.reduce((s,p)=>s+netTotal(p.billing),0);
                const gp = grp.reduce((s,p)=>s+(p.billing?.amountPaid||0),0);
                const gd = Math.max(gt-gp,0);
                const col = key==='paid'?'var(--color-success)':key==='partial'?'var(--color-warning)':'var(--color-danger)';
                return (
                  <div key={key} className="card" style={{ overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, padding:'10px 14px', background:'var(--color-surface)', borderBottom:'1px solid var(--color-border)', borderLeft:`3px solid ${col}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Layers size={13} color="var(--color-ink-faint)" />
                        <span style={{ fontWeight:700, fontSize:13, color:col }}>{label}</span>
                        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {grp.length} patient{grp.length>1?'s':''}</span>
                      </div>
                      <div style={{ display:'flex', gap:14 }}>
                        {[['TOTAL',gt,'var(--color-ink)'],['PAID',gp,'var(--color-success)'],['PENDING',gd,gd>0?'var(--color-warning)':'var(--color-ink-faint)']].map(([l,v,c])=>(
                          <div key={l} style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>{l}</div>
                            <div style={{ fontWeight:800, fontSize:14, color:c }}>₹{fmt(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <BillingCardGroup nested patients={grp} sortKey={sortKey} listMode={listMode} isAdmin={isAdmin} editingId={editingId} setEditingId={setEditingId} correctionId={correctionId} setCorrectionId={setCorrectionId} startEdit={startEdit} startCorrection={startCorrection} setPrintBillPatient={setPrintBillPatient} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} saveBilling={saveBilling} recordPayment={recordPayment} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} applyCorrection={applyCorrection} />
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : listMode === 'compact' ? (
          <div className="card" style={{ overflow:'hidden' }}>
            {patientSummary.map((g, gi) => {
              const lv = afterStatusFilter.filter(p => p.patientUID === g.uid).sort((a,b) => b.tokenDate > a.tokenDate ? 1 : -1)[0];
              const lvTotal   = lv ? netTotal(lv.billing) : 0;
              const lvPending = lv ? pendingBalance(lv.billing) : 0;
              const isEditing    = lv && editingId    === lv._id;
              const isCorrecting = lv && correctionId === lv._id;
              return (
                <div key={g.uid}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', borderTop:gi===0?'none':'1px solid var(--color-border)', background:gi%2===0?'transparent':'var(--color-surface)' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <span style={{ fontWeight:700, fontSize:13 }}>{g.name}</span>
                      <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</span>
                      {g.phone && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}> · {g.phone}</span>}
                      {lv?.tokenDate && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>📅 {lv.tokenDate}</span>}
                      {lv?.appointmentSlot && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-primary)', fontWeight:600 }}>🕐 {lv.appointmentSlot}</span>}
                    </div>
                    {lvPending>0 && <span style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmt(lvPending)} pending</span>}
                    {lv && <span style={{ fontSize:13, fontWeight:800, color:'var(--color-primary)' }}>₹{fmt(lvTotal)}</span>}
                    <div style={{ display:'flex', gap:5, alignItems:'center', flexShrink:0 }}>
                      {lv?.phone && <WhatsAppButton patient={{...lv, _billMessage: buildBillMessage(lv, lvTotal, lv.billing?.amountPaid||0, lvPending)}} slot={lv.appointmentSlot} date={lv.tokenDate} tokenNumber={lv.tokenNumber} size="xs" billMode />}
                      {lv && <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setPrintBillPatient(lv)}><Printer size={12}/></button>}
                      {lv && <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => isEditing ? setEditingId(null) : startEdit(lv)}>{isEditing ? 'Close' : 'Manage'}</button>}
                      {lv && isAdmin && <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(lv)}>{isCorrecting ? 'Close' : 'Correct'}</button>}
                    </div>
                  </div>
                  {isEditing && lv && <div style={{ padding:'0 14px 12px' }}><ManagePanel p={lv} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(lv._id, lv)} onRecordPayment={() => recordPayment(lv)} isAdmin={isAdmin} /></div>}
                  {isCorrecting && lv && isAdmin && <div style={{ padding:'0 14px 12px' }}><CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(lv._id)} /></div>}
                </div>
              );
            })}
          </div>
      ) : listMode === 'grid' ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
            {patientSummary.map((g, i) => {
              const lv = afterStatusFilter.filter(p => p.patientUID === g.uid).sort((a,b) => b.tokenDate > a.tokenDate ? 1 : -1)[0];
              const lvTotal   = lv ? netTotal(lv.billing) : 0;
              const lvPending = lv ? pendingBalance(lv.billing) : 0;
              const isEditing    = lv && editingId    === lv._id;
              const isCorrecting = lv && correctionId === lv._id;
              return (
                <div key={g.uid} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
                  <div className="card tilt3d-card" style={{ padding:14, display:'flex', flexDirection:'column', gap:8 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:6 }}>
                        {g.name}
                      </div>
                      <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</div>
                      {g.phone && <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>📱 {g.phone}</div>}
                      {g.age && <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>{g.age}y · {g.gender}</div>}
                      {lv?.tokenDate && <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>📅 {lv.tokenDate}</div>}
                      {lv?.appointmentSlot && <div style={{ fontSize:12, color:'var(--color-primary)', fontWeight:600 }}>🕐 {lv.appointmentSlot}</div>}
                      {lvPending>0 && <div style={{ fontSize:12, fontWeight:700, color:'var(--color-warning)' }}>₹{fmt(lvPending)} pending</div>}
                    </div>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center', marginTop:'auto', paddingTop:4, borderTop:'1px solid var(--color-border)' }}>
                      {lv && <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)', flex:1 }}>₹{fmt(lvTotal)}</div>}
                      {lv?.phone && <WhatsAppButton patient={{...lv, _billMessage: buildBillMessage(lv, lvTotal, lv.billing?.amountPaid||0, lvPending)}} slot={lv.appointmentSlot} date={lv.tokenDate} tokenNumber={lv.tokenNumber} size="xs" billMode />}
                      {lv && <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setPrintBillPatient(lv)}><Printer size={12}/></button>}
                      {lv && <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => isEditing ? setEditingId(null) : startEdit(lv)}>{isEditing ? 'Close' : 'Manage'}</button>}
                      {lv && isAdmin && <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(lv)}>{isCorrecting ? 'Close' : 'Correct'}</button>}
                    </div>
                  </div>
                  {isEditing && lv && <div style={{ marginTop:4 }}><ManagePanel p={lv} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(lv._id, lv)} onRecordPayment={() => recordPayment(lv)} isAdmin={isAdmin} /></div>}
                  {isCorrecting && lv && isAdmin && <div style={{ marginTop:4 }}><CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(lv._id)} /></div>}
                </div>
              );
            })}
          </div>
      ) : (
        /* ── Default: Group by Patient (list) ── */
        <div style={{ display:'grid', gap:14 }}>
          {patientSummary.map(g => {
            // All visits for this patient in the current filtered set, sorted oldest first
            const fullVisits = afterStatusFilter
              .filter(p => p.patientUID === g.uid)
              .sort((a,b) => (a.tokenDate||'') > (b.tokenDate||'') ? 1 : -1);
            return (
              <div key={g.uid} className="card" style={{ overflow:'hidden', border:'1px solid var(--color-border)' }}>

                {/* ── Patient header — info + old pending only ── */}
                <div style={{ padding:'12px 16px', background:'var(--color-surface)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10, borderBottom:'1px solid var(--color-border)' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:800, fontSize:15, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      {g.name}
                      <span style={{ fontSize:11, fontWeight:400, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2, display:'flex', gap:12, flexWrap:'wrap' }}>
                      {g.phone && <span>📱 {g.phone}</span>}
                      {g.age && <span>{g.age}y, {g.gender}</span>}
                      {fullVisits.length > 0 && <span>📅 {fullVisits[fullVisits.length - 1].tokenDate}</span>}
                      {fullVisits.length > 0 && fullVisits[fullVisits.length - 1].appointmentSlot && (
                        <span style={{ color:'var(--color-primary)', fontWeight:600 }}>🕐 {fullVisits[fullVisits.length - 1].appointmentSlot}</span>
                      )}
                    </div>
                    {(() => {
                      const lv2 = fullVisits[fullVisits.length - 1];
                      if (!lv2) return null;
                      const lv2Paid    = lv2.billing?.amountPaid || 0;
                      const lv2Pending = pendingBalance(lv2.billing);
                      return (
                        <div style={{ fontSize:12, marginTop:2 }}>
                          {lv2Paid > 0 && <span style={{ color:'var(--color-success)' }}>✓ Paid ₹{fmt(lv2Paid)}</span>}
                          {lv2Pending > 0 && <span style={{ marginLeft:12, color:'var(--color-warning)' }}>⏳ Pending ₹{fmt(lv2Pending)}</span>}
                        </div>
                      );
                    })()}
                  </div>

                  {/* ONE set of actions for the whole patient — uses the latest visit */}
                  {(() => {
                    const latestVisit = fullVisits[fullVisits.length - 1];
                    if (!latestVisit) return null;
                    const lTotal   = netTotal(latestVisit.billing);
                    const lPending = pendingBalance(latestVisit.billing);
                    const isEditingLatest    = editingId    === latestVisit._id;
                    const isCorrectingLatest = correctionId === latestVisit._id;
                    return (
                      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'nowrap', flexShrink:0 }}>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>TOTAL</div>
                          <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)' }}>₹{fmt(lTotal)}</div>
                        </div>
                        {latestVisit.phone && (
                          <WhatsAppButton
                            patient={{ ...latestVisit, _billMessage: buildBillMessage(latestVisit, lTotal, latestVisit.billing?.amountPaid||0, lPending) }}
                            slot={latestVisit.appointmentSlot} date={latestVisit.tokenDate}
                            tokenNumber={latestVisit.tokenNumber} size="xs" billMode />
                        )}
                        <button className="btn btn-outline btn-sm" onClick={() => setPrintBillPatient(latestVisit)}>
                          <Printer size={13} /> Print
                        </button>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => isEditingLatest ? setEditingId(null) : startEdit(latestVisit)}>
                          {isEditingLatest ? 'Close' : 'Manage'}
                        </button>
                        {isAdmin && (
                          <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }}
                            onClick={() => isCorrectingLatest ? setCorrectionId(null) : startCorrection(latestVisit)}>
                            <ShieldAlert size={13} />{isCorrectingLatest ? 'Close' : 'Correct'}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── Each visit — full detail ── */}
                {fullVisits.map((p, vi) => {
                  // eslint-disable-next-line no-unused-vars
                  const total        = netTotal(p.billing);
                  // eslint-disable-next-line no-unused-vars
                  const pending      = pendingBalance(p.billing);
                  const isEditing    = editingId    === p._id;
                  const isCorrecting = correctionId === p._id;

                  return (
                    <div key={p._id} style={{ borderTop: vi===0?'none':'1px solid var(--color-border)', background: vi%2===0?'transparent':'var(--color-surface)' }}>

                      {/* Visit row — no per-visit buttons, actions are on patient header */}

                      {/* Inline Manage (billing edit) form */}
                      {isEditing && (
                        <div style={{ padding:'0 16px 14px' }}>
                          <ManagePanel
                            p={p}
                            billingForm={billingForm}
                            setBillingForm={setBillingForm}
                            paymentForm={paymentForm}
                            setPaymentForm={setPaymentForm}
                            saving={saving}
                            recordingPayment={recordingPayment}
                            onSave={() => saveBilling(p._id, p)}
                            onRecordPayment={() => recordPayment(p)}
                            isAdmin={isAdmin}
                          />
                        </div>
                      )}

                      {/* Inline Correct form */}
                      {isCorrecting && isAdmin && (
                        <div style={{ padding:'0 16px 14px' }}>
                          <CorrectionPanel
                            correctionForm={correctionForm}
                            setCorrectionForm={setCorrectionForm}
                            correctionReason={correctionReason}
                            setCorrectionReason={setCorrectionReason}
                            correcting={correcting}
                            onApply={() => applyCorrection(p._id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--color-ink-soft)' }}>
            Showing{' '}
            <strong>{((page - 1) * limit) + 1}</strong>–<strong>{Math.min(page * limit, totalCount)}</strong>
            {' '}of <strong>{totalCount}</strong> records
            {selectedDate !== 'all' && <> for <strong>{selectedDate}</strong></>}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <PagBtn icon={ChevronsLeft}  onClick={() => setPage(1)} disabled={page === 1} title="First page" />
            <PagBtn icon={ChevronLeft}   onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} title="Previous page" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', fontSize: 13, fontWeight: 600 }}>
              Page {page} of {totalPages}
            </div>
            <PagBtn icon={ChevronRight}  onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} title="Next page" />
            <PagBtn icon={ChevronsRight} onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last page" />
          </div>
        </div>
      )}
      {/* Bill print modal */}
      {printBillPatient && (
        <BillPrintModal
          patient={printBillPatient}
          onClose={() => setPrintBillPatient(null)}
        />
      )}
      </>
      )}
      {billingTab === 'pharmacy' && (
        <PharmacyBillingView />
      )}
    </AppLayout>
  );
}

// ── small reusable sub-components ──────────────────────────────────────────────
function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ background: `var(--color-${color}-soft)` }}>
        <Icon size={18} color={`var(--color-${color})`} />
      </div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function NumCell({ label, value, color }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>{label}</div>
      <div style={{ fontWeight: 700, color: color || 'var(--color-ink)' }}>{value}</div>
    </div>
  );
}

function PagBtn({ icon: Icon, onClick, disabled, title }) {
  return (
    <button className="btn btn-icon btn-outline" onClick={onClick} disabled={disabled} title={title}>
      <Icon size={15} />
    </button>
  );
}

// Standard billing management — bill details only, NO amountPaid/status fields.
// Those come from "Record a Payment" below, keeping the history intact.
function ManagePanel({ p, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, onSave, onRecordPayment, isAdmin }) {
  const bf = (field) => (e) => setBillingForm((f) => ({ ...f, [field]: e.target.value }));
  const pf = (field) => (e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }));
  const { showToast } = useToast();

  // Old Balance breakdown — kept as its own local state (not just read from
  // p directly) so a payment applied here updates the list instantly,
  // without waiting on the parent's socket-driven refresh.
  const [oldBalanceVisits, setOldBalanceVisits] = React.useState(p.oldBalanceVisits || []);
  React.useEffect(() => { setOldBalanceVisits(p.oldBalanceVisits || []); }, [p.oldBalanceVisits]);
  function handleOldBalanceSettled(applied) {
    setOldBalanceVisits((prev) => {
      const appliedMap = {};
      applied.forEach((a) => { appliedMap[a.visitId] = a.applied; });
      return prev
        .map((v) => (appliedMap[v.visitId] ? { ...v, pending: Math.max(v.pending - appliedMap[v.visitId], 0) } : v))
        .filter((v) => v.pending > 0);
    });
  }

  // Doctor's treatment cost — an internal clinic cost, kept OFF the patient's
  // bill entirely (stored on patient.doctorCost, not patient.billing). Shown
  // in patient history for admin, and automatically logged as an Expense.
  const [docCostForm, setDocCostForm] = React.useState({
    doctorName: p.doctorCost?.doctorName || p.doctorName || '',
    amount: p.doctorCost?.amount || '',
    notes: p.doctorCost?.notes || '',
  });
  const [savingDocCost, setSavingDocCost] = React.useState(false);
  const dcf = (field) => (e) => setDocCostForm((f) => ({ ...f, [field]: e.target.value }));

  // The "Currently on record" confirmation below is driven from THIS local
  // state (updated directly from the save response), not from the `p` prop.
  // `p` only refreshes once the parent's socket listener catches the
  // 'patient:billing-updated' event and re-fetches — on a slow/flaky
  // connection that round-trip can lag, which is exactly what made this
  // look "delayed": the save had already succeeded, the confirmation just
  // hadn't caught up yet, so retrying kept creating more saves.
  const [savedDoctorCost, setSavedDoctorCost] = React.useState(p.doctorCost || null);

  // Doctor Name is selected from the clinic's actual active doctor profiles,
  // not free-typed — keeps it consistent with names used elsewhere in the app.
  const [doctorOptions, setDoctorOptions] = React.useState([]);
  React.useEffect(() => {
    api.get('/auth/staff')
      .then(r => setDoctorOptions((r.data.staff || []).filter(s => s.role === 'doctor' && s.isActive)))
      .catch(() => setDoctorOptions([]));
  }, []);

  async function saveDoctorCost() {
    if (!docCostForm.doctorName.trim()) { showToast('Please select a doctor.', 'error'); return; }
    if (!docCostForm.amount || Number(docCostForm.amount) <= 0) { showToast('Enter a cost greater than 0.', 'error'); return; }
    setSavingDocCost(true);
    try {
      const res = await api.put(`/patients/${p._id}/doctor-cost`, {
        doctorName: docCostForm.doctorName,
        amount: Number(docCostForm.amount),
        notes: docCostForm.notes,
      });
      // Update the confirmation immediately from the response — don't wait
      // for the socket-driven refresh to catch up.
      setSavedDoctorCost(res.data.patient?.doctorCost || { doctorName: docCostForm.doctorName, amount: Number(docCostForm.amount), notes: docCostForm.notes });
      showToast('Doctor cost saved and logged as an expense.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save doctor cost.', 'error');
    } finally { setSavingDocCost(false); }
  }

  // Preview the auto-derived status as fields change so the user can see
  // what will be set without having to save first.
  const previewNet    = netTotal({
    amount:     Number(billingForm.amount),
    discount:   Number(billingForm.discount),
    taxPercent: Number(billingForm.taxPercent),
  });
  const previewStatus = deriveStatus(previewNet, p.billing?.amountPaid || 0);

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
      {/* Old Balance — what this patient already owed from OTHER (past)
          visits, kept visually separate from this visit's own bill below.
          Shows exactly which visit(s) it's from, and lets it be paid off
          directly here without opening each older visit individually. */}
      {oldBalanceVisits.length > 0 && (
        <OldBalancePanel patient={p} visits={oldBalanceVisits} onSettled={handleOldBalanceSettled}
          thisVisitPending={Math.max(previewNet - (p.billing?.amountPaid || 0), 0)} />
      )}

      {/* Billing details — no paymentStatus or amountPaid dropdowns here */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <F label={<><FileText size={11} style={{ marginRight: 4 }} />Invoice No.</>}>
          <input className="input" value={billingForm.invoiceNumber} onChange={bf('invoiceNumber')} placeholder="INV-1001" />
        </F>
        <F label="Bill Amount (₹)">
          {isAdmin ? (
            <input className="input" type="number" min="0" value={billingForm.amount} onChange={bf('amount')} />
          ) : (
            <input className="input" type="number" value={billingForm.amount} readOnly
              style={{ background:'var(--color-surface)', cursor:'not-allowed', opacity:0.7 }} />
          )}
        </F>
        <F label="Discount (₹)">
          <input className="input" type="number" min="0" value={billingForm.discount} onChange={bf('discount')} placeholder="0" />
        </F>
        <F label="Tax (%)">
          {isAdmin ? (
            <input className="input" type="number" min="0" value={billingForm.taxPercent} onChange={bf('taxPercent')} placeholder="0" />
          ) : (
            <input className="input" type="number" value={billingForm.taxPercent} readOnly
              style={{ background:'var(--color-surface)', cursor:'not-allowed', opacity:0.7 }} />
          )}
        </F>
      </div>
      <F label="Notes">
        <input className="input" value={billingForm.notes} onChange={bf('notes')} placeholder="Insurance ref, treatment notes…" />
      </F>

      {/* Auto-derived status preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-soft)' }}>
        <span>Net total after discount + tax:</span>
        <strong>₹{fmt(previewNet)}</strong>
        <span>→ Payment status will be auto-set to</span>
        <PaymentBadge status={previewStatus} />
      </div>

      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
        <Save size={14} />
        {saving ? 'Saving…' : 'Save Billing Details'}
      </button>

      {/* Doctor Cost — visible to all staff. Internal clinic cost, never shown
          on the patient's bill; visible in patient history and auto-logged
          as an Expense so it doesn't need to be re-entered anywhere else. */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Doctor Cost</div>
        <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginBottom: 10 }}>
          Internal only — never shown on the patient's bill. Shown in patient history and logged automatically as a clinic expense.
        </div>
        {savedDoctorCost?.amount > 0 && (
          <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--color-surface)', borderRadius: 8, fontSize: 12, color: 'var(--color-ink-soft)' }}>
            ✓ Currently on record: Dr. {savedDoctorCost.doctorName} — ₹{fmt(savedDoctorCost.amount)}{savedDoctorCost.notes ? ` (${savedDoctorCost.notes})` : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Doctor Name" style={{ width: 180, marginBottom: 0 }}>
            <select className="select" value={docCostForm.doctorName} onChange={dcf('doctorName')}>
              <option value="">Select doctor…</option>
              {doctorOptions.map(d => (
                <option key={d._id} value={d.displayName || d.username}>{d.displayName || d.username}</option>
              ))}
              {/* Keep whatever was already saved selectable even if that doctor is no longer active */}
              {docCostForm.doctorName && !doctorOptions.some(d => (d.displayName || d.username) === docCostForm.doctorName) && (
                <option value={docCostForm.doctorName}>{docCostForm.doctorName}</option>
              )}
            </select>
          </F>
          <F label="Treatment Cost (₹)" style={{ width: 130, marginBottom: 0 }}>
            <input className="input" type="number" min="0" value={docCostForm.amount} onChange={dcf('amount')} placeholder="0" />
          </F>
          <F label="Notes" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={docCostForm.notes} onChange={dcf('notes')} placeholder="e.g. RCT fee" />
          </F>
          <button className="btn btn-sm" style={{ background: 'var(--color-warning)', color: 'white', alignSelf: 'flex-end', marginBottom: 0 }}
            onClick={saveDoctorCost} disabled={savingDocCost}>
            {savingDocCost ? 'Saving…' : 'Save Doctor Cost'}
          </button>
        </div>
      </div>

      {/* Record a Payment section */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Record a Payment</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Amount (₹)" style={{ width: 120, marginBottom: 0 }}>
            <input className="input" type="number" min="0" value={paymentForm.amount} onChange={pf('amount')} placeholder="0" />
          </F>
          <F label="Method" style={{ width: 130, marginBottom: 0 }}>
            <select className="select" value={paymentForm.method} onChange={pf('method')}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </F>
          <F label="Date" style={{ width: 150, marginBottom: 0 }}>
            <input className="input" type="date" max={todayISO()} value={paymentForm.date || todayISO()} onChange={pf('date')} />
          </F>
          <F label="Note" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={paymentForm.note} onChange={pf('note')} placeholder="Receipt #…" />
          </F>
          <button
            className="btn btn-primary btn-sm"
            onClick={onRecordPayment}
            disabled={recordingPayment}
            style={{ alignSelf: 'flex-end', marginBottom: 0 }}
          >
            <Plus size={14} />
            {recordingPayment ? 'Recording…' : 'Add Payment'}
          </button>
        </div>
        <PaymentHistory history={p.billing?.paymentHistory} patient={p} />
      </div>
    </div>
  );
}

// Old Balance breakdown + payment — shows exactly which past visit(s) a
// patient's carried-forward balance comes from, and lets it be paid off
// directly (applied oldest-visit-first) without opening each older visit.
function OldBalancePanel({ patient, visits, onSettled, thisVisitPending = 0 }) {
  const { showToast } = useToast();
  const total = visits.reduce((s, v) => s + v.pending, 0);
  const combinedTotal = total + thisVisitPending;
  const [showPayForm, setShowPayForm] = React.useState(false);
  const [form, setForm] = React.useState({ amount: '', method: 'cash', note: '', date: todayISO() });
  const [paying, setPaying] = React.useState(false);

  async function submitPayment() {
    if (!form.amount || Number(form.amount) <= 0) { showToast('Enter an amount greater than 0.', 'error'); return; }
    setPaying(true);
    try {
      const res = await api.put(`/patients/${patient._id}/old-balance/payment`, {
        amount: Number(form.amount), method: form.method, note: form.note, paidAt: form.date,
      });
      onSettled(res.data.applied || []);
      const { totalApplied, remainingUnapplied } = res.data;
      showToast(
        remainingUnapplied > 0
          ? `₹${fmt(totalApplied)} applied to Old Balance — ₹${fmt(remainingUnapplied)} was more than was owed and wasn't applied.`
          : `₹${fmt(totalApplied)} applied to Old Balance.`,
        'success'
      );
      setForm({ amount: '', method: 'cash', note: '', date: todayISO() });
      setShowPayForm(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not record this payment.', 'error');
    } finally {
      setPaying(false);
    }
  }

  return (
    <div style={{
      marginBottom: 14, padding: '10px 14px', borderRadius: 8,
      background: 'var(--color-warning-soft)', border: '1px solid var(--color-warning)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <IndianRupee size={16} color="var(--color-warning)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Old Balance (from past visits)
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-ink)' }}>
            ₹{fmt(total)} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-ink-faint)' }}>still owed from before this visit</span>
          </div>
          {thisVisitPending > 0 && (
            <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 4 }}>
              + ₹{fmt(thisVisitPending)} pending on <strong>this visit</strong> ({patient.tokenDate}) ={' '}
              <strong style={{ color: 'var(--color-danger)' }}>₹{fmt(combinedTotal)} total owed across everything</strong>
            </div>
          )}
        </div>
        <button className="btn btn-sm" style={{ background: 'var(--color-warning)', color: 'white' }} onClick={() => setShowPayForm((v) => !v)}>
          {showPayForm ? 'Close' : 'Pay Old Balance'}
        </button>
      </div>

      {/* Breakdown — exactly which visit(s) this comes from */}
      <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
        {visits.map((v) => (
          <div key={v.visitId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-ink-soft)' }}>
            <span>Token #{v.tokenNumber} · {v.tokenDate}</span>
            <span style={{ fontWeight: 700 }}>₹{fmt(v.pending)}</span>
          </div>
        ))}
      </div>

      {showPayForm && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-warning)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Amount (₹)" style={{ width: 120, marginBottom: 0 }}>
            <input className="input" type="number" min="0" max={total} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0" />
          </F>
          <F label="Method" style={{ width: 130, marginBottom: 0 }}>
            <select className="select" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </F>
          <F label="Date" style={{ width: 150, marginBottom: 0 }}>
            <input className="input" type="date" max={todayISO()} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </F>
          <F label="Note" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Receipt #…" />
          </F>
          <button className="btn btn-sm" style={{ background: 'var(--color-warning)', color: 'white', marginBottom: 0 }} onClick={submitPayment} disabled={paying}>
            {paying ? 'Recording…' : 'Record Payment'}
          </button>
        </div>
      )}
    </div>
  );
}

// Admin-only correction panel — has full manual control including paymentStatus
function CorrectionPanel({ correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, onApply }) {
  const cf = (field) => (e) => setCorrectionForm((f) => ({ ...f, [field]: e.target.value }));

  const previewNet    = netTotal({
    amount:     Number(correctionForm.amount),
    discount:   Number(correctionForm.discount),
    taxPercent: Number(correctionForm.taxPercent),
  });
  const previewStatus = deriveStatus(previewNet, Number(correctionForm.amountPaid));

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '2px solid var(--color-warning)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <ShieldAlert size={16} color="var(--color-warning)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)' }}>Admin Payment Correction</span>
        <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>— Use only to fix entries made in error. All changes are logged.</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <F label="Bill Amount (₹)"><input className="input" type="number" value={correctionForm.amount} onChange={cf('amount')} /></F>
        <F label="Discount (₹)"><input className="input" type="number" value={correctionForm.discount} onChange={cf('discount')} /></F>
        <F label="Tax (%)"><input className="input" type="number" value={correctionForm.taxPercent} onChange={cf('taxPercent')} /></F>
        <F label="Amount Paid (₹)"><input className="input" type="number" value={correctionForm.amountPaid} onChange={cf('amountPaid')} /></F>
        <F label="Payment Method">
          <select className="select" value={correctionForm.paymentMethod} onChange={cf('paymentMethod')}>
            <option value="none">Not specified</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="upi">UPI</option>
            <option value="insurance">Insurance</option>
            <option value="other">Other</option>
          </select>
        </F>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-soft)' }}>
        <span>Net total after correction:</span>
        <strong>₹{fmt(previewNet)}</strong>
        <span>→ Status will be auto-set to</span>
        <PaymentBadge status={previewStatus} />
      </div>
      <F label="Invoice Number">
        <input className="input" value={correctionForm.invoiceNumber || ''} onChange={cf('invoiceNumber')} placeholder="INV-XXXX" />
      </F>
      <F label="Notes">
        <input className="input" value={correctionForm.notes || ''} onChange={cf('notes')} />
      </F>
      <F label="Reason for correction (required)">
        <input
          className="input"
          value={correctionReason}
          onChange={(e) => setCorrectionReason(e.target.value)}
          placeholder="e.g. Wrong amount entered — correcting ₹1500 to ₹1200"
          style={{ borderColor: !correctionReason ? 'var(--color-warning)' : undefined }}
        />
      </F>
      <button
        className="btn btn-sm"
        style={{ background: 'var(--color-warning)', color: 'white' }}
        onClick={onApply}
        disabled={correcting || !correctionReason.trim()}
      >
        <ShieldAlert size={14} />
        {correcting ? 'Applying…' : 'Apply Correction'}
      </button>
    </div>
  );
}

// Payment history with patient details and full timestamp
function PaymentHistory({ history, patient }) {
  // Self-contained: keeps its own copy of history so an edit reflects
  // instantly, independent of the parent's socket-driven refresh (the same
  // lesson learned from Doctor Cost's save confirmation feeling delayed).
  const [localHistory, setLocalHistory] = React.useState(history || []);
  React.useEffect(() => { setLocalHistory(history || []); }, [history]);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const { showToast } = useToast();

  if (!localHistory?.length) return null;

  function startEdit(entry) {
    setEditingId(entry._id);
    setEditForm({
      amount: entry.amount || 0,
      method: entry.method || 'cash',
      note:   entry.note || '',
      date:   entry.paidAt ? toLocalISODate(entry.paidAt) : todayISO(),
    });
  }

  async function saveEdit(entryId) {
    setSaving(true);
    try {
      const res = await api.put(`/patients/${patient._id}/billing/payment/${entryId}`, {
        amount: Number(editForm.amount) || 0,
        method: editForm.method,
        note:   editForm.note,
        paidAt: editForm.date,
      });
      setLocalHistory(res.data.patient?.billing?.paymentHistory || localHistory);
      showToast('Payment updated.', 'success');
      setEditingId(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update this payment.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
        <History size={13} /> Payment History
      </div>

      {/* Patient summary at the top of history, for context when printing / referencing */}
      <div style={{ fontSize: 12, padding: '8px 10px', background: 'var(--color-primary-soft)', borderRadius: 6, marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--color-primary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <User size={12} /> {patient?.name}
        </span>
        {patient?.patientUID && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{patient.patientUID}</span>}
        {patient?.age && <span>{patient.age}y, {patient.gender}</span>}
        {patient?.phone && <span>📱 {patient.phone}</span>}
        {patient?.tokenDate && <span>Visit: {patient.tokenDate}</span>}
        {patient?.reasonForVisit && <span style={{ fontStyle: 'italic', color: 'var(--color-primary)', opacity: 0.8 }}>{patient.reasonForVisit}</span>}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {[...localHistory].reverse().map((entry) => (
          <div
            key={entry._id}
            style={{
              padding: '8px 10px',
              background: entry.amount === 0 ? 'var(--color-warning-soft)' : 'var(--color-bg)',
              borderRadius: 6,
            }}
          >
            {editingId === entry._id ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <F label="Amount (₹)" style={{ width: 100, marginBottom: 0 }}>
                  <input className="input" type="number" min="0" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} />
                </F>
                <F label="Method" style={{ width: 110, marginBottom: 0 }}>
                  <select className="select" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.method} onChange={e => setEditForm(f => ({ ...f, method: e.target.value }))}>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="insurance">Insurance</option>
                    <option value="other">Other</option>
                  </select>
                </F>
                <F label="Date" style={{ width: 140, marginBottom: 0 }}>
                  <input className="input" type="date" style={{ fontSize: 12, padding: '4px 8px' }} max={todayISO()}
                    value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                </F>
                <F label="Note" style={{ flex: 1, minWidth: 100, marginBottom: 0 }}>
                  <input className="input" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
                </F>
                <button className="btn btn-primary btn-sm" style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={() => saveEdit(entry._id)} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-outline btn-sm" style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ flex: 1 }}>
                  {entry.amount > 0
                    ? <strong>₹{entry.amount.toLocaleString('en-IN')}</strong>
                    : <span style={{ color: 'var(--color-warning)', fontWeight: 700 }}>⚙ Correction</span>}
                  {entry.amount > 0 && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.method}</span>}
                  {entry.note && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.note}</span>}
                </span>
                <div style={{ textAlign: 'right', color: 'var(--color-ink-faint)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <Clock size={11} />
                      {fmtDateTime(entry.paidAt)}
                    </div>
                    {entry.recordedBy && <div style={{ fontSize: 11 }}>by {entry.recordedBy}</div>}
                  </div>
                  {entry.amount > 0 && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }} title="Edit this payment"
                      onClick={() => startEdit(entry)}>
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Tiny field wrapper
function F({ label, children, style }) {
  return (
    <div className="field" style={{ marginBottom: 12, ...style }}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
